// WebSocket session hook for /sessions/:id/stream.
//
// Buffers AgentEvents into a deterministic timeline. Streams of
// assistant_delta with the same toolUseId? — actually deltas don't carry an
// id; we coalesce consecutive deltas that are not separated by another
// event into a single ASSISTANT_TURN entry. Tool calls collapse the start /
// input / output triple into a single TOOL_CALL entry keyed by toolUseId so
// the renderer can reuse the same card.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type {
  AgentAsk,
  AgentEvent,
  BreakdownChildSummary,
  BridgeState,
  FigmaAllowRefusal,
  FigmaLinkKind,
  FigmaLinkRow,
  FigmaLinkState,
  GateBlockHints,
  ImplementStage,
  InvestVerdict,
  Proposal,
  SlotVerdict,
} from "@loom/shared";
import type { LocBucket, SizingConfidence, SizingRisk } from "@loom/shared/readiness";

import { useT } from "../i18n/index.js";
import {
  designSceneSchema,
  SCENE_VERSION,
  type DesignScene,
} from "../lib/design-scene";
import {
  foldAgentWork,
  INITIAL_AGENT_WORK,
  phaseSinceClientMs,
  touchesAgentWork,
  type AgentWork,
  type AgentWorkState,
} from "./agent-work";
import { clearIdleDelete, trackIdleDelete } from "./idle-delete";
import type { SessionStatus } from "./session-status";
import { httpDelete } from "../lib/http";
import {
  openWsSession,
  WS_CLOSE_DENIED,
  type BreakdownOpPayload,
  type ClientFrame,
  type ServerControl,
  type WsClient,
} from "../lib/ws";
import { wsDiag } from "../lib/ws-diag";
import {
  reportClientEvent,
  setClientTraceSession,
} from "../lib/client-trace";
import {
  createRemountSafeStore,
  type RemountSafeStore,
} from "../state/remount-safe-state";
import {
  INITIAL_SANDBOX_STATUS,
  type SandboxStatusSnapshot,
} from "../state/sandbox-status-store";

export type TimelineEntry =
  | {
      readonly kind: "assistant_turn";
      readonly id: string;
      readonly text: string;
    }
  | {
      readonly kind: "tool_call";
      readonly id: string;
      readonly toolUseId: string;
      readonly toolName?: string;
      readonly input?: unknown;
      readonly output?: unknown;
      readonly ok?: boolean;
    }
  | {
      readonly kind: "lifecycle";
      readonly id: string;
      readonly phase: string;
      readonly handler: string;
      readonly outcome:
        | { readonly kind: "continue" }
        | { readonly kind: "augment"; readonly systemNote: string }
        | {
            readonly kind: "block";
            readonly reason: string;
            readonly hints?: GateBlockHints;
          };
    }
  | {
      readonly kind: "proposal";
      readonly id: string;
      readonly proposalId: string;
      readonly proposal: Proposal;
      readonly state: "pending" | "confirmed" | "rejected";
    }
  | {
      readonly kind: "role";
      readonly id: string;
      readonly toolUseId: string;
      readonly role: string;
      readonly task: string;
      readonly summary?: string;
    }
  | {
      readonly kind: "error";
      readonly id: string;
      readonly message: string;
      readonly terminal: boolean;
      /** Machine-readable error class (e.g. the `edit_*` codes the ready-screen
       *  field-edit path emits). Absent on errors that carry only prose. */
      readonly code?: string;
      /** Built by this client, and `message` is already catalog copy in the
       *  user's language. A server error never sets it: Focus renders its
       *  `code`, not its `message` (`focus/errorCopy.ts`). */
      readonly localized?: true;
    }
  | {
      readonly kind: "budget_exhausted";
      readonly id: string;
      readonly budget:
        | "tokens"
        | "wall_time"
        | "spend"
        | "executor_cpu"
        | "executor_wall";
      readonly limit: number;
      readonly used: number;
    }
  | {
      readonly kind: "tools_unavailable";
      readonly id: string;
      readonly reason: "refused" | "unreachable";
      readonly detail: string;
    }
  | {
      readonly kind: "user_message";
      readonly id: string;
      readonly text: string;
      /**
       * The flow this turn opened or continued — the wire event's own
       * `issueIid`, carried through verbatim. Absent on a flow-less turn (free
       * chat, a system nudge), which is a different fact from "issue 0": the
       * refine resume evidence compares it against the issue it is mounted on,
       * so an absent key must never match.
       */
      readonly issueIid?: number;
      /**
       * The delivery id the sender minted for this turn, echoed back on the
       * wire event. A composer holding the user's own bytes releases on it —
       * the row's arrival IS the confirmation, and unlike the ack it survives
       * a replay and a server that never advertised acks. Absent on any turn
       * this client did not send.
       */
      readonly clientMsgId?: string;
    }
  | {
      readonly kind: "coding_progress";
      readonly id: string;
      readonly entry:
        | { readonly kind: "user_prompt"; readonly text: string }
        | {
            readonly kind: "file_touched";
            readonly path: string;
            readonly op: "read" | "edit" | "write";
          }
        | { readonly kind: "commit"; readonly sha: string; readonly message: string }
        | { readonly kind: "bash"; readonly command: string }
        | { readonly kind: "assistant_text"; readonly text: string };
    }
  | {
      readonly kind: "readiness_card";
      readonly id: string;
      readonly issueIid: number;
      readonly verdicts: ReadonlyArray<SlotVerdict>;
      readonly dor: {
        readonly ok: boolean;
        readonly missing: ReadonlyArray<string>;
      };
      readonly dod: {
        readonly ok: boolean;
        readonly missing: ReadonlyArray<string>;
      };
      readonly invest: {
        readonly verdicts: ReadonlyArray<InvestVerdict>;
        readonly score: number;
      };
      readonly methodologySkipped: boolean;
      readonly allGreen: boolean;
      readonly acceptToken?: string;
      /** Highest event-log seq the card's grounding scan had visible (see the
       *  wire schema in lib/ws.ts). Absent on cards from unstamped rows. */
      readonly groundedThroughSeq?: number;
      /** In-flight ready-screen field edits the agent hasn't folded back in yet.
       *  RefineFlow renders these fields amber and disarms Accept until they
       *  clear. Absent on cards from old servers (deploy-skew). */
      readonly pendingEdits?: ReadonlyArray<{ readonly field: string }>;
    }
  | {
      /**
       * ONE typed pre-card question from the agent (`question_pending`). The
       * channel that exists because the readiness card cannot carry a question
       * before it exists: during the boot window the agent could only ask in
       * PROSE, and a prose answer is unattributable.
       *
       * Upserted in place BY `askId` (like `readiness_card` by issueIid), so an
       * attach re-emit or a replay converges rather than stacking duplicates.
       * `resolved` is the fold of `question_resolved` — a resolved ask stays on
       * the timeline (it is durable history) but no surface pins it.
       */
      readonly kind: "agent_ask";
      readonly id: string;
      readonly askId: string;
      readonly issueIid: number;
      /** The readiness field this settles, or null for a free-standing ask
       *  (scoping, disambiguation) that maps to no spec field. */
      readonly field: string | null;
      readonly question: string;
      readonly help?: string;
      readonly optional?: boolean;
      /**
       * The ask's SHAPE. A `"concern"` is the agent flagging a problem with
       * something already settled — a contradiction between two verified fields,
       * a value that no longer fits what it read in the repo — rather than asking
       * for something missing. It may be bound to an already-verified field, it
       * survives the card going all-green, and it is always skippable.
       *
       * Named `askKind`, not `kind`: `kind` is this union's own discriminant.
       * Optional in both directions of a deploy skew — absent means a plain
       * question, which is also where an unrecognised wire value normalises to.
       */
      readonly askKind?: "question" | "concern";
      /** Loom's draft answer rows for this ask — the typed channel that keeps
       *  examples out of the question text. Display-only; the rows are an
       *  answer only once the user sends them. */
      readonly draft?: ReadonlyArray<string>;
      /**
       * WHAT this ask is about, when it is not about a readiness field.
       *
       * Today the one member is the Figma sketch: a `field: null` ask carrying
       * `{ kind: "figma_preview", previewId }` is the "is this the screen you
       * meant?" step, and `previewId` is the opaque handle the client hands
       * straight back to the preview route. An unrecognised `kind` normalises
       * to nothing here, so the ask degrades to a plain question rather than
       * vanishing.
       *
       * This is the THIRD of the three seams that must each declare it —
       * `question-event.ts` server-side, `lib/ws.ts`'s wire mirror, and this
       * fold, which rebuilds the entry key by key and would otherwise drop it
       * with no type error (the `widget` / `draft` / `gateRefusal` lesson).
       */
      readonly subject?: {
        readonly kind: "figma_preview";
        readonly previewId: string;
      };
      readonly resolved: boolean;
      /**
       * WHY it resolved, folded from `question_resolved.reason`. `"superseded"`
       * is the only durable trace a flow boundary leaves of a PRE-CARD question
       * it retired — without it a surface cannot tell "the user answered this"
       * from "switching issues took it away", and the second is the one the user
       * has to be told about. Absent on an older server's tombstone.
       */
      readonly resolvedReason?:
        | "answered"
        | "declined"
        | "carded"
        | "superseded";
    }
  | {
      /** The epic/sub-issue breakdown proposal — replaced in place per issue
       *  like `readiness_card` (a re-propose after an adjust supersedes it). */
      readonly kind: "breakdown_card";
      readonly id: string;
      readonly issueIid: number;
      readonly noSplit: boolean;
      readonly rationale: string;
      /** Server-split verbatim AC rows; child `coveredCriteria` index into it. */
      readonly criteria: ReadonlyArray<string>;
      readonly children: ReadonlyArray<BreakdownChildSummary>;
      /** Verbatim must-haves the user retired at the split step — reversible. */
      readonly parked?: ReadonlyArray<string>;
      readonly breakdownToken?: string;
      /** CAS precondition for every typed op on this card. Absent ⇒ this
       *  proposal predates typed ops and the card stays on the prose lane. */
      readonly childrenHash?: string;
    };

export interface ReauthState {
  readonly required: boolean;
  readonly gitlabHost: string;
}

export interface BridgeStatusState {
  readonly state: BridgeState;
  readonly pipelineId?: number;
  readonly etaSeconds?: number;
  readonly message?: string;
}

export type SessionMode = "pm" | "coding";

export interface BranchState {
  readonly commits: number;
  readonly summary: string | null;
}

/** Latest autonomous-implement pipeline frame. `null` for non-implement
 *  sessions (no `implement_stage` event ever lands). */
export interface ImplementSnapshot {
  readonly issueIid: number;
  readonly stage: ImplementStage;
  readonly status: "active" | "done" | "failed";
  readonly detail: string | null;
  readonly mrUrl: string | null;
  /** Review-app links surfaced once the deploy stage completes. */
  readonly environments: ReadonlyArray<{ readonly name: string; readonly url: string }>;
  /** Set when the DoR gate halted the run — drives the proceed/refine card. */
  readonly dorBlocked: {
    readonly missing: ReadonlyArray<string>;
    readonly haltKind?: "epic";
    readonly childIids?: ReadonlyArray<number>;
  } | null;
}

export interface UseWsSession {
  readonly timeline: ReadonlyArray<TimelineEntry>;
  /**
   * `idle_killed` is the terminal state of the human-idle kill: after a stretch
   * with no human activity we showed a countdown (see `idleCountdownEndsAt`) and,
   * on its expiry, hard-DELETEd the session so the server cancels the CI job and
   * frees the sandbox runner. There is no resume — the timeline stays visible
   * behind a terminal dialog and the user must start a fresh session. Distinct
   * from `closed` (a real drop we auto-reconnect from) and `expired` (a dead
   * session we cannot reopen).
   *
   * `denied` is the access-refusal terminal: the server refused this IDENTITY
   * (approval revoked, or a session URL opened by another user), not this
   * session. Separate from `expired` precisely because the recoveries differ —
   * "start a new session" fixes an expiry and does nothing for a denial, so
   * offering it would just relocate the loop into the restart button. Use
   * `isSessionDead` (`./session-status`) for the cases that treat both alike.
   */
  readonly status: SessionStatus;
  readonly reauth: ReauthState;
  readonly bridge: BridgeStatusState;
  /** Phase 6 — orchestrator-emitted sandbox phase, surfaced as the
   *  SandboxStatus pill in the chat header. Carries the same wire signal
   *  as `bridge` but with the richer phase taxonomy
   *  (triggering / waiting-for-runner / booting / ready / degraded /
   *  suspended / terminated). */
  readonly sandbox: SandboxStatusSnapshot;
  /** Server-authoritative conversation mode (Phase 5). */
  readonly mode: SessionMode;
  /**
   * Coding-mode session-branch state. `commits > 0` surfaces the
   * "Create merge request" affordance in the UI (Phase 7).
   */
  readonly branch: BranchState;
  /**
   * Server-derived agent run state. `true` while a sandbox turn is executing,
   * `false` once it stops (waiting on the user). Defaults to `false`, so a
   * server that doesn't emit `agent_turn` degrades to "always idle" rather than
   * hanging anything that waits on it.
   */
  readonly agentBusy: boolean;
  /**
   * Whether `agentBusy` is the SERVER's answer for this socket AND the attach
   * replay has landed: the attach snapshot (`agent_turn`, sent right after
   * `session_attached`) has arrived since the socket last (re)connected, and
   * the burst that follows it — the FULL replay of the session's bus — has gone
   * quiet (`ATTACH_SETTLE_QUIET_MS`, bounded by `ATTACH_SETTLE_MAX_MS`).
   * `agentBusy` is not reset on a reconnect — the pre-drop value carries through
   * the replay window — so on its own it cannot say "the agent is idle NOW"; a
   * first load reads `false` before the server has said anything. Anything that
   * treats idle as a fact reads both: a red offered as Loom's wording once
   * SETTLED, and every held batch that goes out on its own.
   *
   * The replay half is what a reload needs. The server sends the snapshot
   * BEFORE the replay, so "the snapshot landed" alone let a batch restored from
   * sessionStorage go out while the page still held no card and no ask — an
   * empty queue reads "every question answered" — and the card that replayed a
   * moment later re-asked the step the user had just answered.
   */
  readonly busyKnown: boolean;
  /**
   * What the server says about the agent's current turn: when the busy stretch
   * began, the latest tool it called in it, and why the last one stopped when
   * the bridge said (`./agent-work`). All on the browser's clock, converted
   * without comparing clocks. Carried through a reconnect like `agentBusy`;
   * the attach snapshot re-derives it. Optional so a test double that builds a
   * `UseWsSession` by hand need not name it — the Focus wait line reads a
   * missing one as "no detail".
   */
  readonly agentWork?: AgentWork;
  /**
   * Latest deterministic readiness-card apply outcome, or null before any
   * accept. The refine surface watches this to leave the "applying…" state.
   */
  readonly applyResult: {
    readonly issueIid: number;
    readonly ok: boolean;
    readonly webUrl?: string;
    /** A failure's wire text: the code, then static English. Focus never
     *  renders it (`focus/errorCopy.ts`); the dashboard card does. */
    readonly error?: string;
    /** A failure's closed reason (`APPLY_ERROR_CODES`), what Focus renders
     *  copy for. Absent from an older server. */
    readonly code?: string;
    /** Present only when the write left the `## Implementation details`
     *  section out. `planDropReason` is a closed server catalogue the surface
     *  renders LOCAL copy for — never prose off the wire. */
    readonly planSectionWritten?: boolean;
    readonly planDropReason?: string;
  } | null;
  /**
   * Set once this session hit its budget cap (F5). Terminal for the agent —
   * the focus surfaces render a dedicated end state instead of an endless
   * spinner. Derived from the durable, replay-worthy `budget_exhausted` event.
   */
  /**
   * Set once the sandbox proved it cannot reach Loom's MCP tools. Terminal:
   * without them the agent can never call `propose_card`, so no readiness card
   * will ever arrive and the focus surfaces must stop waiting for one. Derived
   * from the durable, replay-worthy `tools_unavailable` event.
   */
  readonly toolsUnavailable: {
    readonly reason: "refused" | "unreachable";
    readonly detail: string;
  } | null;
  readonly budgetExhausted: {
    readonly budget:
      | "tokens"
      | "wall_time"
      | "spend"
      | "executor_cpu"
      | "executor_wall";
    readonly limit: number;
    readonly used: number;
  } | null;
  /**
   * Latest end-of-refine implementation-plan outcome, or null before any
   * request. `ready` carries the non-technical business summary for the
   * business-validation step; `unavailable` (no live sandbox, or the agent
   * didn't produce a plan) tells the refine surface to skip straight to Accept.
   */
  readonly planResult:
    | {
        readonly kind: "ready";
        readonly issueIid: number;
        readonly businessSummary: string;
        /** The persisted plan revision this summary renders. Echoed back on
         *  `sendReadinessPlanConfirm` so a "yes, that matches" can only confirm
         *  the text the user was shown. Absent on a server that predates it —
         *  the surface then withholds the confirm rather than guess. */
        readonly planRevision?: number;
      }
    | {
        readonly kind: "unavailable";
        readonly issueIid: number;
        readonly reason: string;
      }
    | null;
  /**
   * Latest sizing tail-step outcome, or null before any plan round. `ready`
   * carries the path-free aggregate + the server's `splitRecommended` policy
   * bit; `unavailable` tells the surface to skip the split offer entirely.
   */
  readonly sizingResult:
    | {
        readonly kind: "ready";
        readonly issueIid: number;
        readonly sizeSummary: string;
        readonly overall: {
          readonly filesTouched: number;
          readonly locBucket: LocBucket;
          readonly confidence: SizingConfidence;
        };
        readonly risks: ReadonlyArray<SizingRisk>;
        readonly criteriaCount: number;
        readonly splitRecommended: boolean;
        readonly oversizedSingleCriterion: boolean;
        /** `splitRecommended` describes criteria that have since moved — treat
         *  it as unknown and ask, never as a decision. Optional so a fixture or
         *  an older shape stays assignable; absent reads as "the verdict
         *  stands", which is the pre-flag behaviour. */
        readonly verdictStale?: boolean;
      }
    | {
        readonly kind: "unavailable";
        readonly issueIid: number;
        readonly reason: string;
      }
    | null;
  /**
   * Breakdown proposal-turn degrade (`readiness_breakdown_unavailable`) —
   * the proposal itself arrives as a `breakdown_card` timeline entry.
   */
  readonly breakdownUnavailable: {
    readonly issueIid: number;
    readonly reason: string;
  } | null;
  /**
   * Accumulated per-child creation progress for the running breakdown apply
   * (from `readiness_breakdown_progress` frames). Rebuilt by replay; null
   * before any apply.
   */
  readonly breakdownProgress: {
    readonly issueIid: number;
    readonly total: number;
    readonly createdByLocalId: ReadonlyMap<
      string,
      { readonly iid: number; readonly title: string }
    >;
    readonly linkedLocalIds: ReadonlySet<string>;
  } | null;
  /**
   * Terminal breakdown apply outcome. `ok:false` carries progress — receipts
   * survive server-side, so re-sending the same accept frame resumes.
   */
  readonly breakdownResult: {
    readonly issueIid: number;
    readonly ok: boolean;
    readonly children?: ReadonlyArray<{
      readonly localId: string;
      readonly iid: number;
      readonly title: string;
      readonly url?: string;
    }>;
    readonly error?: string;
    readonly progress?: {
      readonly created: number;
      readonly linked: number;
      readonly total: number;
    };
  } | null;
  /**
   * Send a user message. Returns the minted `clientMsgId` — the handle the
   * caller can watch in `ackedIds` for the server's delivery confirmation
   * (and the bus seq of the echo). The frame is held in the outbox until the
   * server acks it; a send into a dead-but-OPEN socket redelivers on the next
   * attach. `opts.scope` binds the entry to an opaque send-scope (see
   * `setSendScope`) so a stale redelivery can't cross surfaces. `opts.flow`
   * tags the turn with its issue identity (Phase 1 — flow boundaries); it rides
   * the outboxed frame so it survives reconnect redelivery.
   */
  readonly send: (
    text: string,
    opts?: {
      readonly scope?: string;
      readonly flow?: {
        readonly issueIid: number;
        readonly boundary?: boolean;
        /** Display metadata for the discovery list — see `UserMessageFrame`. */
        readonly issueTitle?: string;
      };
      /**
       * The byte-exact subset of `text` the user themselves typed or picked —
       * set ONLY by a machine-composed turn (a wizard seed). The grounding gate
       * then quotes that span instead of the whole turn, so the AI-derived
       * title / project-map paths / boilerplate around it cannot come back as
       * certified user evidence. Omitted on every human turn (which keeps the
       * scan on the full text). `""` is a real value — "nothing here is
       * quotable" — so it is passed through, not dropped as falsy.
       */
      readonly evidenceText?: string;
      /**
       * Explicit "New chat" (Phase 1 — flow boundaries) for a pure-chat surface
       * with no issue identity: the server starts a fresh CLI conversation. On a
       * flow surface, pass `flow: { ..., boundary: true }` instead.
       */
      readonly newChat?: boolean;
    },
  ) => string;
  /**
   * Send a slash command without echoing it as a user message in the
   * timeline. Used by the mode toggle (`/code`, `/pm`). Ack-released like
   * every frame (mode flips produce no bus echo, so the ingress ack is their
   * only delivery confirmation).
   */
  readonly sendSystem: (text: string) => void;
  /**
   * Accept an all-green readiness card. Returns whether the structured frame
   * reached an OPEN socket NOW (the retry UI keys on it); independently the
   * frame is outboxed and redelivered until acked — the server-side apply is
   * idempotent on a consumed token.
   */
  readonly sendReadinessAccept: (issueIid: number, acceptToken: string) => boolean;
  /**
   * Edit a field on the final "ready to apply" preview. Widget fields
   * (priority / milestone) resolve server-side in one round-trip; free-text
   * fields fold in via a directed agent re-propose. Returns whether the frame
   * reached an OPEN socket; outboxed + ack-released like every structured frame.
   */
  readonly sendReadinessFieldEdit: (
    issueIid: number,
    field: string,
    text: string,
  ) => boolean;
  /**
   * Withdraw a pending free-text edit ("Keep the previous value") — the server
   * drops the pending entry and re-emits the card with Accept re-armed.
   */
  readonly sendReadinessFieldEditCancel: (
    issueIid: number,
    field: string,
  ) => boolean;
  /**
   * SKIP an optional field. Persists the decision server-side so it survives a
   * remount and reaches the agent — a skip that never left the browser is why a
   * skipped `milestone` was re-asked for the rest of the flow.
   */
  readonly sendReadinessFieldDecline: (
    issueIid: number,
    field: string,
  ) => boolean;
  /**
   * Answer (and/or decline) typed pre-card asks. Returns the minted
   * `clientMsgId` — NOT `.delivered` — because this frame is a grounding TURN
   * like `send`: RefineFlow's seq-keyed flush suppression keys on the id to
   * match the server's ack against a card's `groundedThroughSeq`, exactly as it
   * does for `ws.send`. `text` is the byte-exact turn; `answers` is attribution
   * only (see QuestionAnswerFrame).
   */
  readonly sendQuestionAnswer: (
    issueIid: number,
    text: string,
    answers: ReadonlyArray<{
      readonly askId: string;
      readonly field: string | null;
      readonly text: string;
      /** Question or concern — see `QuestionAnswerFrame`. Sent explicitly on
       *  every answer: the server fails CLOSED to the concern framing when it
       *  cannot tell, so leaving it off would give a plain answer the wrong
       *  prompt whenever the server's ask mirror is empty (instance recycle). */
      readonly kind?: "question" | "concern";
    }>,
    declinedAskIds?: ReadonlyArray<string>,
    extra?: {
      /** Card-slot answers in the SAME batch. The routed prompt is built from
       *  the attributed arrays, not from `text`, so a card answer omitted here
       *  is one the agent never reads — and re-asks. */
      readonly cardAnswers?: ReadonlyArray<{
        readonly field: string;
        readonly text: string;
      }>;
      /** A free-composer utterance owned by no ask and no pinned slot. Routed
       *  as an unattributed block so it cannot be silently dropped. */
      readonly freeText?: string;
      /** How many distinct answers went into `text`. Diagnostic only — the
       *  server warns when it can attribute fewer, which is exactly the
       *  "answered but never routed" failure. */
      readonly answeredCount?: number;
      /** Surface that owns this send. Without it the outbox's cross-issue drop
       *  cannot fire and a stale answer redelivers into another issue's flow. */
      readonly scope?: string;
    },
  ) => string;
  /**
   * Request the end-of-refine implementation simulation. Returns whether the
   * frame reached an OPEN socket. `correction` carries the user's clarification
   * when re-running after a business-summary mismatch.
   */
  readonly sendReadinessPlanRequest: (
    issueIid: number,
    correction?: string,
  ) => boolean;
  /**
   * "Yes, that matches" on the business summary — the one user decision behind
   * the implementation plan, and the only thing that lets apply append the
   * `## Implementation details` section. `planRevision` is the revision the
   * validate screen rendered; the server refuses to confirm anything else, so a
   * newer plan that landed under the screen cannot be confirmed by proxy — and
   * that refusal now reaches the surface (RefineFlow's post-confirm branch)
   * instead of dying against the `plan_loading` tail gate.
   * Returns whether the frame reached an OPEN socket; outboxed + ack-released
   * like every structured frame (a redelivered confirm re-flips the same row,
   * because the confirm leaves the plan's revision alone).
   */
  readonly sendReadinessPlanConfirm: (
    issueIid: number,
    planRevision: number,
  ) => boolean;
  /**
   * "Keep going" past the budget wall: raise the sandbox's spend ceiling by
   * `addUsd` and carry on with everything already spent and grounded. Returns
   * whether the frame reached an OPEN socket.
   */
  readonly sendBudgetExtend: (addUsd: number) => boolean;
  /** Request the epic/sub-issue breakdown proposal. `force` when the user
   *  asked for a split the policy did not recommend. */
  readonly sendReadinessBreakdownRequest: (
    issueIid: number,
    force?: boolean,
  ) => boolean;
  /** Confirm the breakdown (one action: parent apply + children creation,
   *  server-sequenced). Idempotent/receipts-resume on redelivery or retry. */
  readonly sendBreakdownAccept: (
    issueIid: number,
    acceptToken: string,
    breakdownToken: string,
  ) => boolean;
  /**
   * Send a TYPED adjustment to a proposed split. Deterministic ops (rename,
   * remove, merge, move, add, park) are executed by the server with no agent
   * turn at all; `split_further` / `rewrite_scope` / `free_text` open a scoped
   * model turn instead.
   *
   * `baseChildrenHash` is read from the card entry at call time, not cached —
   * it is the compare-and-swap that makes an action composed against a
   * superseded proposal fail loudly instead of landing on a different piece.
   */
  readonly sendBreakdownOp: (
    issueIid: number,
    baseChildrenHash: string,
    op: BreakdownOpPayload,
    groundingText?: string,
  ) => boolean;
  /** Outcome of the last typed op for a given issue, or null. Cleared when a
   *  fresh proposal lands. */
  readonly breakdownOpResult: {
    readonly issueIid: number;
    readonly ok: boolean;
    readonly error?: string;
    readonly message?: string;
  } | null;
  /**
   * Every Figma link the server has said anything about in this session, in
   * first-seen order, one entry per `linkId`, folded from the replay-worthy
   * `figma_link_state` event. Empty until the first one arrives.
   *
   * A SNAPSHOT, never an authority: the chip renders what the server last said,
   * and every action on it is a frame, not a local mutation. Read a single link
   * with `selectFigmaLink`, and the session-wide "nicht mehr fragen" latch with
   * `figmaSessionDeclined` — both exported from this module.
   */
  readonly figmaLinks: ReadonlyArray<FigmaLinkSnapshot>;
  /**
   * "Später" on a Figma connect prompt. `scope: "session"` is the second
   * decline and silences every link in this session, present and future.
   *
   * MUST be sent rather than kept in component state: the client wipes its
   * timeline on reconnect, so a decline the server does not hold comes back as
   * a fresh connect prompt on a chip the user already dismissed. Returns
   * whether the frame reached an OPEN socket.
   */
  readonly sendFigmaConnectDeclined: (
    linkId: string,
    scope: "link" | "session",
  ) => boolean;
  /** "Allow this file in this project" on a `not_allowed` chip — the server
   *  appends the file key to `.loom.yml`. Addressed by `linkId`; the client has
   *  never been told the key and cannot name one. */
  readonly sendFigmaAllowFile: (linkId: string) => boolean;
  /** The sketch verdict: "Loom read the screen I meant", or not. Addressed by
   *  the opaque `previewId` the ask handed the client; carries no text, and
   *  writes nothing on the readiness card either way. */
  readonly sendFigmaPreviewVerdict: (
    previewId: string,
    askId: string,
    verdict: "right" | "wrong",
  ) => boolean;
  /**
   * This deployment SERVES the design sketch — the server's own
   * `session_attached` capability (`figmaPreview`), latched per session.
   *
   * Whether `GET …/figma/preview/:previewId` is registered depends on the
   * preview flag AND the design store AND the account resolver, none of which a
   * client can see. Gated on a client flag instead, every "Show the sketch" on
   * every read chip fetched a fastify 404 and rendered "only the words were kept
   * for this design, not the layout" — a sentence about the DATA that a missing
   * ROUTE cannot support. `false` against an older server, which is the safe
   * direction: no affordance rather than a broken one.
   */
  readonly figmaPreviewEnabled: boolean;
  /**
   * The picture THIS CHAT had for a handle, or `null` — in which case ask the
   * route, which serves Loom's NEWEST copy of the frame under a dated caption.
   *
   * `handle` is either a read's `previewId` (what a flow step and an ask hold)
   * or a chip's `linkId` (what a chat holds); both are keys into the same fold
   * of the replay-worthy `figma_scene` event. Hand the answer straight to
   * `FigmaPreview`'s `scene` prop — a non-null one makes the widget skip the GET
   * entirely, which is what keeps a restored chat off `unknown_link` (the
   * in-process link registry after an instance recycle), off `no_raw` (the raw
   * bytes' 7-day horizon) and, above all, off bytes a LATER read of the same
   * frame overwrote.
   *
   * `null` is not an error and not a claim about the data: a chat from before
   * the event existed, or one whose scene the event log's FIFO cap evicted, has
   * no stored picture and the fallback is exactly today's behaviour.
   */
  readonly figmaSceneFor: (handle: string) => DesignScene | null;
  /** Latest autonomous-implement pipeline frame, or null. */
  readonly implement: ImplementSnapshot | null;
  /**
   * Keyed bag for UI state that must survive the reconnect timeline wipe.
   * The attach effect's `setTimeline([])` + full replay unmounts every
   * timeline-rendered component for the replay window; anything mount-local in
   * them (widget drafts, link selections, one-shot "Sent" latches) dies with
   * the subtree. This store lives with the HOOK (it survives reconnects, and
   * is cleared on a genuine sessionId change), so components re-seed from it
   * on remount via `useRemountSafeState`. The session owner provides it to the
   * timeline subtree through `RemountSafeStoreProvider`.
   */
  readonly uiState: RemountSafeStore;
  /** Resolve a DoR-halted implement run by proceeding anyway. Returns whether
   *  the frame reached an OPEN socket. */
  readonly sendImplementProceed: () => boolean;
  /**
   * Delivery confirmations: clientMsgId → bus seq of the user_message echo
   * (null for acks without one — mode flips, structured frames). An id
   * appearing here means the server's ingress accepted the frame; RefineFlow
   * keys its seq-aware flush suppression on the seq.
   */
  readonly ackedIds: ReadonlyMap<string, number | null>;
  /**
   * Bind the active send-scope. Entries sent with a `scope` are dropped at
   * redelivery time when this no longer matches — the guard that keeps a
   * stale refine answer from grounding against a different issue's card.
   */
  readonly setSendScope: (scope: string | null) => void;
  readonly reconnect: () => void;
  /**
   * Reconnect after a server-side `POST /sessions/:id/reset`. Unlike
   * `reconnect()`, this also clears `uiState` and bumps `resetEpoch`: reset
   * keeps the sessionId but rebuilds the bus from seq 0, so persisted entry-id
   * keys and seq-ordered latches are invalid afterwards.
   */
  readonly resetSession: () => void;
  /** Counts resetSession() calls. Include it in any client-side latch key that
   *  orders entries by seq, so a reset invalidates the latch. */
  readonly resetEpoch: number;
  /**
   * Wall-clock deadline (ms epoch) at which the human-idle kill fires, or null
   * when no countdown is running. Non-null drives the warning dialog's live
   * countdown; the socket stays OPEN throughout so any gesture (or the dialog's
   * "I'm still here" button → `dismissIdle`) cancels the kill without a reconnect.
   */
  readonly idleCountdownEndsAt: number | null;
  /** Cancel a pending human-idle kill (the user is back). No-op when no countdown
   *  is running. Any tracked activity gesture has the same effect. */
  readonly dismissIdle: () => void;
  /**
   * Resume a session AFTER the human-idle hard-kill (the terminal `idle_killed`
   * state). The idle DELETE only evicts the sandbox — it does NOT stamp
   * `endedAt`, so the session stays genuinely resumable; the caller re-triggers a
   * fresh pipeline via `POST /sessions/:id/reconnect` (which resumes the agent's
   * conversation with `LOOM_RESUME=true`) and then calls this to clear the
   * client-side `idleKilled` latch, letting the WS-attach effect re-run and
   * reopen the socket onto the same session. No-op unless a kill has latched.
   */
  readonly resumeFromIdleKill: () => void;
  readonly clearReauth: () => void;
}

/**
 * One Figma link as the UI sees it — the fold of every `figma_link_state` row
 * that has named this `linkId` in the current replay.
 *
 * `"unknown"` is a CLIENT-ONLY member of both unions and never rides the wire.
 * The zod mirror parses `state` and `kind` as bare strings on purpose (a
 * `safeParse` failure discards the whole frame and every chip in it), so a
 * newer server's thirteenth state has to land somewhere: it lands here, as one
 * neutral chip, instead of costing the user the whole batch.
 */
export interface FigmaLinkSnapshot {
  readonly linkId: string;
  readonly state: FigmaLinkState | "unknown";
  readonly kind: FigmaLinkKind | "unknown";
  readonly label?: string;
  readonly folderName?: string;
  /** The frame a read opened — the user's own screen. Sticky. */
  readonly frameName?: string;
  readonly lastModifiedIso?: string;
  readonly fileVersion?: string;
  readonly counts?: {
    readonly nodes?: number;
    readonly frames?: number;
    readonly textNodes?: number;
  };
  readonly commentCount?: number;
  readonly retryAfterS?: number;
  readonly retryAtIso?: string;
  readonly declineScope?: "link" | "session";
  /** The last "Allow this file" was refused, and why. Not sticky. */
  readonly allowRefusal?: FigmaAllowRefusal;
  /** The last "Allow this file" put the file on the list. Not sticky. */
  readonly allowed?: true;
  /** Where the link sat in the message that first showed the chip. Sticky. */
  readonly inMessage?: {
    readonly index: number;
    readonly count: number;
    readonly skipped?: number;
  };
  /** On a `read` chip: the latest re-read was refused, and why. Not sticky. */
  readonly refreshRefusal?: {
    readonly state: FigmaLinkState | "unknown";
    readonly retryAtIso?: string;
  };
  /**
   * On a `read` chip: the digest came out of Loom's own copy of an earlier
   * read, not out of a Figma call in this turn. `readAtIso` is when Figma was
   * last actually read. NOT sticky — a read that really reached Figma omits it
   * and the line clears.
   */
  readonly fromStore?: { readonly readAtIso: string };
  /**
   * Echo seq of the NEWEST user message that carried this link. Sticky,
   * newest-present wins: a read's notice omits it and it stays put, while the
   * same link pasted again in a later message makes that message the newest.
   * A surface that shows one chip per link (a refine step) renders it there; a
   * chat renders it under every message in {@link placements}. Absent on
   * replays written before the field existed.
   */
  readonly sourceSeq?: number;
  /**
   * EVERY message that carried this link, oldest first, each with the place the
   * link had in THAT message. `sourceSeq` and `inMessage` above are the newest
   * of these. A chat renders the chip under each one, so pasting a link again
   * leaves the earlier message its chip and its "1 of 4". Folded from the
   * scan's rows (each one that names a `sourceSeq` adds or refreshes one), so a
   * reload, a replay and a second tab rebuild the same list. Absent until a row
   * with a `sourceSeq` arrives.
   */
  readonly placements?: ReadonlyArray<FigmaLinkPlacement>;
  /** Bus seq of the event that last touched this row. Ordering / debugging. */
  readonly seq: number;
}

/** One message a Figma link rode in. See {@link FigmaLinkSnapshot.placements}. */
export interface FigmaLinkPlacement {
  /** The echo seq of that message. */
  readonly sourceSeq: number;
  /** The link's place in that message, as the scan of it wrote. */
  readonly inMessage?: {
    readonly index: number;
    readonly count: number;
    readonly skipped?: number;
  };
}

/** Written as a Record, not a Set, so the compiler REQUIRES every member of
 *  `FigmaLinkState`: a state added in `@loom/shared` and forgotten here would
 *  otherwise normalise to `"unknown"` and be invisible, which is exactly the
 *  drift the `_MissingEventType` guard exists to prevent one level up. */
const FIGMA_LINK_STATES: Readonly<Record<FigmaLinkState, true>> = {
  recognized: true,
  needs_frame: true,
  not_connected: true,
  declined: true,
  unsupported_kind: true,
  not_allowed: true,
  loading: true,
  read: true,
  unreachable: true,
  frame_missing: true,
  rate_limited: true,
  seat_limited: true,
  token_expired: true,
  degraded: true,
  too_large: true,
  upstream_error: true,
  config_malformed: true,
};
const FIGMA_LINK_KINDS: Readonly<Record<FigmaLinkKind, true>> = {
  file: true,
  design: true,
  board: true,
  proto: true,
  slides: true,
  deck: true,
  community: true,
};

const normaliseFigmaState = (raw: string): FigmaLinkState | "unknown" =>
  Object.prototype.hasOwnProperty.call(FIGMA_LINK_STATES, raw)
    ? (raw as FigmaLinkState)
    : "unknown";

const normaliseFigmaKind = (raw: string | undefined): FigmaLinkKind | "unknown" =>
  raw !== undefined && Object.prototype.hasOwnProperty.call(FIGMA_LINK_KINDS, raw)
    ? (raw as FigmaLinkKind)
    : "unknown";

const EMPTY_FIGMA_LINKS: ReadonlyArray<FigmaLinkSnapshot> = [];

/**
 * Merge one wire row onto the current list. UPSERT by `linkId`, in place, and
 * a PATCH rather than a replace: the row is the newest-present value for each
 * field and everything it omits is sticky (see `FigmaLinkRow`). That is what
 * lets the server answer a `figma_connect_declined` — where it holds a bare
 * `linkId` and nothing else — with `{ linkId, state: "declined" }` without
 * blanking the chip's label and kind.
 *
 * Append-only would be wrong for the same reason it is wrong for
 * `question_pending`: the client wipes its timeline on reconnect and the full
 * replay re-feeds every row, so the identical link would stack up once per
 * reconnect.
 */
/**
 * The placements after one row. A row that names its message adds that message,
 * or refreshes the place the link had there; a row that names none (a read, a
 * decline, a reconcile patch) keeps the list as it was.
 */
const placementsAfter = (
  prior: FigmaLinkSnapshot | null,
  row: FigmaLinkRow,
): { readonly placements?: ReadonlyArray<FigmaLinkPlacement> } => {
  const before = prior?.placements ?? [];
  const at = row.sourceSeq;
  if (at === undefined) return before.length > 0 ? { placements: before } : {};
  const inMessage =
    row.inMessage ?? before.find((p) => p.sourceSeq === at)?.inMessage;
  const here: FigmaLinkPlacement = {
    sourceSeq: at,
    ...(inMessage !== undefined ? { inMessage } : {}),
  };
  return {
    placements: [...before.filter((p) => p.sourceSeq !== at), here].sort(
      (a, b) => a.sourceSeq - b.sourceSeq,
    ),
  };
};

const mergeFigmaLink = (
  links: ReadonlyArray<FigmaLinkSnapshot>,
  row: FigmaLinkRow,
  seq: number,
): ReadonlyArray<FigmaLinkSnapshot> => {
  const idx = links.findIndex((l) => l.linkId === row.linkId);
  const prior = idx === -1 ? null : links[idx]!;
  const kind = normaliseFigmaKind(row.kind);
  const next: FigmaLinkSnapshot = {
    linkId: row.linkId,
    state: normaliseFigmaState(row.state),
    // A row that omits `kind` keeps the one already on screen.
    kind: kind === "unknown" ? (prior?.kind ?? "unknown") : kind,
    seq,
    ...(row.label !== undefined
      ? { label: row.label }
      : prior?.label !== undefined
        ? { label: prior.label }
        : {}),
    ...(row.folderName !== undefined
      ? { folderName: row.folderName }
      : prior?.folderName !== undefined
        ? { folderName: prior.folderName }
        : {}),
    ...(row.frameName !== undefined
      ? { frameName: row.frameName }
      : prior?.frameName !== undefined
        ? { frameName: prior.frameName }
        : {}),
    ...(row.lastModifiedIso !== undefined
      ? { lastModifiedIso: row.lastModifiedIso }
      : prior?.lastModifiedIso !== undefined
        ? { lastModifiedIso: prior.lastModifiedIso }
        : {}),
    ...(row.fileVersion !== undefined
      ? { fileVersion: row.fileVersion }
      : prior?.fileVersion !== undefined
        ? { fileVersion: prior.fileVersion }
        : {}),
    ...(row.counts !== undefined
      ? { counts: row.counts }
      : prior?.counts !== undefined
        ? { counts: prior.counts }
        : {}),
    ...(row.commentCount !== undefined
      ? { commentCount: row.commentCount }
      : prior?.commentCount !== undefined
        ? { commentCount: prior.commentCount }
        : {}),
    // NOT sticky: a `Retry-After` is about the attempt that produced it, and a
    // stale countdown on a chip that has since read fine is a lie the user can
    // watch tick down.
    ...(row.retryAfterS !== undefined ? { retryAfterS: row.retryAfterS } : {}),
    ...(row.retryAtIso !== undefined ? { retryAtIso: row.retryAtIso } : {}),
    // NOT sticky either, and for the same reason: each describes ONE allow
    // attempt. A refusal that outlived a later read would hide the button on a
    // chip the user may now allow; an "allowed" that outlived a later read
    // would promise a read that has since come back with something else.
    ...(row.allowRefusal !== undefined
      ? { allowRefusal: row.allowRefusal }
      : {}),
    ...(row.allowed === true ? { allowed: true as const } : {}),
    // Sticky: where the link sat in its message does not change.
    ...(row.inMessage !== undefined
      ? { inMessage: row.inMessage }
      : prior?.inMessage !== undefined
        ? { inMessage: prior.inMessage }
        : {}),
    // NOT sticky, same reason as `retryAfterS`: it describes the latest attempt
    // at a re-read, and a read that worked after it must not keep the line.
    // Every server row that keeps a `read` chip's line carries it again.
    ...(row.refreshRefusal !== undefined
      ? {
          refreshRefusal: {
            state: normaliseFigmaState(row.refreshRefusal.state),
            ...(row.refreshRefusal.retryAtIso !== undefined
              ? { retryAtIso: row.refreshRefusal.retryAtIso }
              : {}),
          },
        }
      : {}),
    // NOT sticky, for the same reason `refreshRefusal` is not: it describes
    // where THIS answer came from. A later read that really reached Figma sends
    // no `fromStore`, and the "from Loom's copy" line has to disappear with it
    // — a chip still claiming a copy after a live read is the same class of lie
    // as a countdown ticking on a chip that has since read fine.
    ...(row.fromStore !== undefined ? { fromStore: row.fromStore } : {}),
    ...(row.declineScope !== undefined
      ? { declineScope: row.declineScope }
      : prior?.declineScope !== undefined
        ? { declineScope: prior.declineScope }
        : {}),
    ...(row.sourceSeq !== undefined
      ? { sourceSeq: row.sourceSeq }
      : prior?.sourceSeq !== undefined
        ? { sourceSeq: prior.sourceSeq }
        : {}),
    ...placementsAfter(prior, row),
  };
  if (idx === -1) return [...links, next];
  return [...links.slice(0, idx), next, ...links.slice(idx + 1)];
};

/**
 * Apply one event's worth of rows. Exported so the merge contract the UI builds
 * against is testable without standing up a socket — and so the hook and the
 * test can never fold differently.
 */
export const applyFigmaLinkRows = (
  links: ReadonlyArray<FigmaLinkSnapshot>,
  rows: ReadonlyArray<FigmaLinkRow>,
  seq: number,
): ReadonlyArray<FigmaLinkSnapshot> =>
  rows.reduce((acc, row) => mergeFigmaLink(acc, row, seq), links);

/** Look one link up by id. Exported so a surface can read the chip it is about
 *  to act on without re-deriving the scan. */
export const selectFigmaLink = (
  links: ReadonlyArray<FigmaLinkSnapshot>,
  linkId: string,
): FigmaLinkSnapshot | null =>
  links.find((l) => l.linkId === linkId) ?? null;

/**
 * True once the user has declined for the WHOLE session ("nicht mehr fragen").
 * Derived from the replayed rows rather than latched in component state, so it
 * survives the reconnect timeline wipe like every other server-held fact.
 *
 * Keyed on `declineScope`, not on `state === "declined"`: only a decline ever
 * writes `declineScope`, and the fold keeps it sticky — so it still says what
 * the user said after a later read has moved that chip on to `read`. Keyed on
 * the state, one successful read of the declined link would silently lift the
 * user's "don't ask again".
 */
export const figmaSessionDeclined = (
  links: ReadonlyArray<FigmaLinkSnapshot>,
): boolean => links.some((l) => l.declineScope === "session");

/**
 * The picture one Figma read produced, as this chat holds it.
 *
 * Folded from the replay-worthy `figma_scene` event, which is what makes a
 * RESTORED chat draw the bytes that conversation actually read. The route
 * (`GET …/figma/preview/:previewId`) serves Loom's newest copy of the frame
 * under a dated caption and stays the fallback for a chat with no stored scene.
 */
export interface FigmaSceneSnapshot {
  /** The read's handle — the same value the ask's `subject.previewId` carries.
   *  Random and session-scoped; never a file key. */
  readonly previewId: string;
  /** Bus seq of the event that wrote this entry. Latest wins; see
   *  {@link applyFigmaSceneEvent}. */
  readonly seq: number;
  readonly scene: DesignScene;
}

const EMPTY_FIGMA_SCENES: ReadonlyMap<string, FigmaSceneSnapshot> = new Map();

/**
 * A `figma_scene` event's own fields, re-checked at runtime before the fold
 * trusts any of them.
 *
 * `@loom/shared`'s `AgentEvent` union DOES declare `FigmaSceneEvent`, and it
 * types `scene` as a `DesignScene`. The wire does not agree: `lib/ws.ts` parses
 * that field as `z.unknown()` on purpose, so an unreadable draw list costs the
 * entry here instead of the frame there. `agentEventSchema` is annotated as
 * `z.ZodType<AgentEvent>`, so the compiler believes a parsed scene has already
 * arrived when what actually arrived is whatever the server sent.
 *
 * That gap is why this accessor stays now that the type exists: it is the seam
 * where a permissive wire is turned back into something the fold may rely on.
 * `previewId` must be a non-empty string or there is no key to file under, and
 * `linkIds` is filtered rather than trusted because `lib/ws.ts` gives it a
 * `.catch([])` — a malformed list reaches this point as a value, not a throw.
 * The draw list itself is parsed by the caller against `designSceneSchema`.
 */
const figmaSceneWireOf = (
  event: AgentEvent,
): {
  readonly previewId: string;
  readonly linkIds: ReadonlyArray<string>;
  readonly scene: unknown;
} | null => {
  const raw = event as {
    readonly type?: unknown;
    readonly previewId?: unknown;
    readonly linkIds?: unknown;
    readonly scene?: unknown;
  };
  if (raw.type !== "figma_scene") return null;
  if (typeof raw.previewId !== "string" || raw.previewId === "") return null;
  const linkIds = Array.isArray(raw.linkIds)
    ? raw.linkIds.filter((v): v is string => typeof v === "string" && v !== "")
    : [];
  return { previewId: raw.previewId, linkIds, scene: raw.scene };
};

/** Whether this event is a scene at all — the fold's own `return`, kept beside
 *  the accessor so the two can never disagree about the discriminant. */
export const isFigmaSceneEvent = (event: AgentEvent): boolean =>
  (event as { readonly type?: unknown }).type === "figma_scene";

/**
 * Merge one `figma_scene` event into the map. Exported for the same reason
 * `applyFigmaLinkRows` is: the contract the UI builds against is testable
 * without standing up a socket, and the hook and the test can never fold
 * differently.
 *
 * KEYED TWICE — under the read's `previewId`, which is what a flow step holds,
 * and under every `linkId` the read was about, which is what a chat chip holds.
 * Two reads of DIFFERENT frames therefore key their own scenes, and a chip they
 * share moves on to the newest, which is the right behaviour for a chip.
 *
 * A HANDLE IS PER FRAME, NOT PER READ, and it matters here. The server's
 * `notePreviewRead` keys `previewId` by `(fileKey, nodeId)` and REUSES the
 * handle, so re-reading one frame emits a second scene under the SAME
 * `previewId` at a higher seq — and by the rule below it replaces the first
 * under every key. A flow step whose question is open on that frame therefore
 * sees its picture swap to the newer bytes mid-answer. That is defensible (the
 * newer bytes are the ones Loom just read, and `readAtIso` re-dates the caption
 * with them) but it is NOT "the picture as of the ask", and a comment here once
 * claimed it was. The frozen property this whole lane exists for is the one
 * against TIME — a restored chat draws what it was shown rather than today's
 * copy of the frame — not one against a re-read inside the same conversation.
 *
 * LATEST SEQ WINS, and a lower seq never displaces a higher one. The full replay
 * re-feeds every event in order, but a live event can arrive interleaved with a
 * replay, so the guard is on the seq rather than on arrival order.
 *
 * COSTS THE ENTRY, NEVER THE FRAME. A scene this bundle cannot read — an
 * unparseable shape, or a `sceneVersion` a newer projector bumped — is dropped
 * here and the handle simply has no stored picture, which falls the host back
 * to the GET, which re-projects. `lib/ws.ts` passes `scene` through as
 * `unknown` precisely so that decision lands here and not in a `safeParse` whose
 * failure would discard the whole frame.
 */
export const applyFigmaSceneEvent = (
  scenes: ReadonlyMap<string, FigmaSceneSnapshot>,
  event: AgentEvent,
): ReadonlyMap<string, FigmaSceneSnapshot> => {
  const wire = figmaSceneWireOf(event);
  if (wire === null) return scenes;
  const parsed = designSceneSchema.safeParse(wire.scene);
  if (!parsed.success) return scenes;
  if (parsed.data.sceneVersion !== SCENE_VERSION) return scenes;
  const snapshot: FigmaSceneSnapshot = {
    previewId: wire.previewId,
    seq: event.seq,
    scene: parsed.data,
  };
  let next: Map<string, FigmaSceneSnapshot> | null = null;
  for (const key of [wire.previewId, ...wire.linkIds]) {
    const held = (next ?? scenes).get(key);
    if (held !== undefined && held.seq >= event.seq) continue;
    next ??= new Map(scenes);
    next.set(key, snapshot);
  }
  return next ?? scenes;
};

const eventId = (e: AgentEvent): string => `${e.sessionId}#${e.seq}`;

/**
 * An ask's SUBJECT, normalised to the one member this build knows.
 *
 * `lib/ws.ts` parses `subject` off the wire, but `@loom/shared`'s `AgentAsk`
 * does not (yet) declare it, and `agentEventSchema` is annotated as
 * `z.ZodType<AgentEvent>` — so the field is present at runtime and invisible to
 * the compiler. Read through this ONE accessor rather than a cast at the fold:
 * the shape is re-checked here at runtime, so a malformed subject costs the
 * subject and never the ask.
 *
 * When `AgentAsk` gains the field upstream this collapses to `ask.subject` and
 * the runtime check stays (a permissive wire is still a permissive wire).
 */
const askSubjectOf = (
  ask: AgentAsk,
): { readonly kind: "figma_preview"; readonly previewId: string } | null => {
  const raw = (ask as { subject?: unknown }).subject;
  if (raw === null || typeof raw !== "object") return null;
  const { kind, previewId } = raw as { kind?: unknown; previewId?: unknown };
  if (kind !== "figma_preview") return null;
  if (typeof previewId !== "string" || previewId === "") return null;
  return { kind: "figma_preview", previewId };
};

/**
 * MINOR-2 instrumentation only. What assistant-visible text an event carries,
 * and by which LANE. Byte length only — never the text.
 *
 * Deliberately NOT inside `applyEvent`: that is a pure reducer invoked from
 * inside a `setTimeline` updater, and `<React.StrictMode>` double-invokes
 * updaters in dev — instrumentation for a DUPLICATE-row bug that manufactures
 * duplicate entries answers nothing. `onEvent` runs once per event.
 */
/**
 * A stream-json message a SUBAGENT wrote: the CLI tags every message of a Task
 * tool's agent with `parent_tool_use_id`, the id of the tool call it runs
 * under, and leaves it `null` on the conversation's own messages.
 */
export const isSubagentMessage = (p: Record<string, unknown>): boolean =>
  typeof p.parent_tool_use_id === "string" && p.parent_tool_use_id.length > 0;

/**
 * The whole text of a turn that answered nothing. The sandbox's model writes
 * "No response requested." when a turn it was in is cut short by the next
 * prompt Loom itself sends (a plan request after an all-green card) — live on
 * the issue page #172 it was the only reply to the user's criteria, in English,
 * in a German session. It is not a reply to the user. Whole text only, so a
 * real sentence that happens to contain the words is kept.
 */
export const isNoReplyNotice = (text: string): boolean =>
  /^no response requested\.?$/iu.test(text.trim());

const assistantTextArrival = (
  event: AgentEvent,
):
  | { readonly lane: "delta"; readonly chars: number }
  | { readonly lane: "whole"; readonly chars: number }
  | null => {
  if (event.type === "assistant_delta") {
    return { lane: "delta", chars: event.text.length };
  }
  if (event.type !== "bridge_stream") return null;
  if (!("payload" in event) || event.payload === undefined) return null;
  const p = event.payload as Record<string, unknown> | null;
  if (p === null || typeof p !== "object") return null;
  if (p.type === "assistant") {
    // Mirrors `applyEvent`: a subagent's message renders nothing.
    if (isSubagentMessage(p)) return null;
    // Mirrors the extraction in `applyEvent`'s `bridge_stream` branch — the
    // JOINED length, which is what actually renders.
    const content = (p.message as { content?: unknown } | null | undefined)
      ?.content;
    if (!Array.isArray(content)) return null;
    const chars = content
      .filter(
        (b): b is { type: "text"; text: string } =>
          typeof b === "object" &&
          b !== null &&
          (b as { type?: unknown }).type === "text" &&
          typeof (b as { text?: unknown }).text === "string",
      )
      .reduce((n, b) => n + b.text.length, 0);
    return chars > 0 ? { lane: "whole", chars } : null;
  }
  if (
    p.type === "content_block_delta" &&
    typeof (p.delta as { text?: unknown } | undefined)?.text === "string"
  ) {
    return { lane: "delta", chars: (p.delta as { text: string }).text.length };
  }
  return null;
};

// Exported so the event → timeline mapping is testable on its own. The refine
// resume evidence reads `user_message.issueIid`, and a key that silently stops
// riding along here is invisible from the outside: the flow would simply wait
// out its grace and re-seed.
export const applyEvent = (
  timeline: TimelineEntry[],
  event: AgentEvent,
): TimelineEntry[] => {
  switch (event.type) {
    case "assistant_delta": {
      const last = timeline[timeline.length - 1];
      if (last !== undefined && last.kind === "assistant_turn") {
        const updated: TimelineEntry = {
          kind: "assistant_turn",
          id: last.id,
          text: last.text + event.text,
        };
        return [...timeline.slice(0, -1), updated];
      }
      return [...timeline, { kind: "assistant_turn", id: eventId(event), text: event.text }];
    }
    case "tool_use_start":
      return [
        ...timeline,
        {
          kind: "tool_call",
          id: eventId(event),
          toolUseId: event.toolUseId,
          toolName: event.toolName,
        },
      ];
    case "tool_use_input":
    case "tool_use_output": {
      const idx = timeline.findIndex(
        (e) => e.kind === "tool_call" && e.toolUseId === event.toolUseId,
      );
      if (idx === -1) return timeline;
      const existing = timeline[idx]!;
      if (existing.kind !== "tool_call") return timeline;
      const updated: TimelineEntry =
        event.type === "tool_use_input"
          ? { ...existing, input: event.input }
          : { ...existing, output: event.output, ok: event.ok };
      return [...timeline.slice(0, idx), updated, ...timeline.slice(idx + 1)];
    }
    case "lifecycle_decision":
      return [
        ...timeline,
        {
          kind: "lifecycle",
          id: eventId(event),
          phase: event.phase,
          handler: event.handler,
          outcome: event.outcome,
        },
      ];
    case "role_spawn":
      return [
        ...timeline,
        {
          kind: "role",
          id: eventId(event),
          toolUseId: event.toolUseId,
          role: event.role,
          task: event.task,
        },
      ];
    case "role_return": {
      const idx = timeline.findIndex((e) => e.kind === "role" && e.toolUseId === event.toolUseId);
      if (idx === -1) return timeline;
      const existing = timeline[idx]!;
      if (existing.kind !== "role") return timeline;
      return [
        ...timeline.slice(0, idx),
        { ...existing, summary: event.summary },
        ...timeline.slice(idx + 1),
      ];
    }
    case "error":
      return [
        ...timeline,
        {
          kind: "error",
          id: eventId(event),
          message: event.message,
          terminal: event.terminal,
          ...(event.code !== undefined ? { code: event.code } : {}),
        },
      ];
    case "budget_exhausted":
      // Dedup belt (F5): the server emits one durable event, but a restart
      // re-feed could re-append — keep exactly one entry.
      if (timeline.some((e) => e.kind === "budget_exhausted")) return timeline;
      return [
        ...timeline,
        {
          kind: "budget_exhausted",
          id: eventId(event),
          budget: event.kind,
          limit: event.limit,
          used: event.used,
        },
      ];
    case "tools_unavailable":
      // Same dedup belt as the budget wall above — terminal, emitted once, but
      // a replay re-feed must not stack duplicates.
      if (timeline.some((e) => e.kind === "tools_unavailable")) return timeline;
      return [
        ...timeline,
        {
          kind: "tools_unavailable",
          id: eventId(event),
          reason: event.reason,
          detail: event.detail,
        },
      ];
    case "reauth_required":
      return timeline; // handled via reauth state below
    case "proposal_pending":
      return [
        ...timeline,
        {
          kind: "proposal",
          id: eventId(event),
          proposalId: event.proposalId,
          proposal: event.proposal,
          state: "pending",
        },
      ];
    case "proposal_confirmed":
    case "proposal_rejected": {
      const idx = timeline.findIndex(
        (e) => e.kind === "proposal" && e.proposalId === event.proposalId,
      );
      if (idx === -1) return timeline;
      const existing = timeline[idx]!;
      if (existing.kind !== "proposal") return timeline;
      const nextState: "confirmed" | "rejected" =
        event.type === "proposal_confirmed" ? "confirmed" : "rejected";
      return [
        ...timeline.slice(0, idx),
        { ...existing, state: nextState },
        ...timeline.slice(idx + 1),
      ];
    }
    case "readiness_card_pending": {
      // Re-emitted on every propose_card call — replace the card for this
      // issue in place so the verdict badges flip red->green as the user
      // answers the needs_input questions.
      //
      // `verdicts` passes through WHOLESALE, and that is load-bearing: do not
      // rewrite it key-by-key. Every optional extra on a verdict (`widget`,
      // `draft`, `gateRefusal`, `revised`, `declined`) reaches Focus only
      // because this hop copies the array by reference — a mapped rebuild here
      // would silently drop the next one added, exactly as the schema mirror in
      // lib/ws.ts dropped `declined`, and with the same invisibility (every
      // Focus suite builds `TimelineEntry` literals directly). Pinned in
      // apps/web/test/ws-session.test.ts.
      const entry: TimelineEntry = {
        kind: "readiness_card",
        id: eventId(event),
        issueIid: event.issueIid,
        verdicts: event.verdicts,
        dor: event.dor,
        dod: event.dod,
        invest: event.invest,
        methodologySkipped: event.methodologySkipped,
        allGreen: event.allGreen,
        ...(event.acceptToken !== undefined
          ? { acceptToken: event.acceptToken }
          : {}),
        ...(event.groundedThroughSeq !== undefined
          ? { groundedThroughSeq: event.groundedThroughSeq }
          : {}),
        ...(event.pendingEdits !== undefined
          ? { pendingEdits: event.pendingEdits }
          : {}),
      };
      const idx = timeline.findIndex(
        (e) => e.kind === "readiness_card" && e.issueIid === event.issueIid,
      );
      if (idx === -1) return [...timeline, entry];
      return [...timeline.slice(0, idx), entry, ...timeline.slice(idx + 1)];
    }
    case "question_pending": {
      // One entry PER ASK, upserted by askId — the same in-place replace the
      // readiness card uses, for the same reason: the server re-emits pendings
      // on every attach, and the full replay re-feeds them, so append-only
      // would stack the identical question several times on screen.
      let next = timeline;
      for (const ask of event.asks) {
        const idx = next.findIndex(
          (e) => e.kind === "agent_ask" && e.askId === ask.askId,
        );
        const existing = idx === -1 ? null : next[idx]!;
        const entry: TimelineEntry = {
          kind: "agent_ask",
          id: `${eventId(event)}#${ask.askId}`,
          askId: ask.askId,
          issueIid: event.issueIid,
          field: ask.field,
          question: ask.question,
          ...(ask.help !== undefined ? { help: ask.help } : {}),
          ...(ask.optional !== undefined ? { optional: ask.optional } : {}),
          // Normalised HERE, so the wire stays permissive (`z.string()`) while
          // everything downstream sees a closed set: anything that is not the
          // literal "concern" folds to a plain question rather than dropping the
          // ask. A third kind from a newer server therefore costs presentation,
          // never the question itself.
          ...(ask.kind === "concern" ? { askKind: "concern" as const } : {}),
          ...(ask.draft !== undefined && ask.draft.length > 0
            ? { draft: ask.draft }
            : {}),
          // Normalised HERE against the one subject this build knows, exactly
          // as `askKind` is: an unrecognised subject word folds to "no
          // subject" (a plain question), never to a dropped ask.
          ...(() => {
            const subject = askSubjectOf(ask);
            return subject === null ? {} : { subject };
          })(),
          // A resolution ALWAYS wins over a (re-)pending, whichever order they
          // arrive in — this fold has to agree with `deriveOpenAsks`, whose
          // order-independence exists so a truncated replay range can't leave a
          // zombie question pinned forever.
          resolved:
            existing !== null && existing.kind === "agent_ask"
              ? existing.resolved
              : false,
          // Carried with `resolved` for the same reason: a re-pending that
          // arrives after the tombstone must not erase WHY it was retired.
          ...(existing !== null &&
          existing.kind === "agent_ask" &&
          existing.resolvedReason !== undefined
            ? { resolvedReason: existing.resolvedReason }
            : {}),
        };
        next =
          idx === -1
            ? [...next, entry]
            : [...next.slice(0, idx), entry, ...next.slice(idx + 1)];
      }
      return next;
    }
    case "question_resolved": {
      let next = timeline;
      for (const askId of event.askIds) {
        const idx = next.findIndex(
          (e) => e.kind === "agent_ask" && e.askId === askId,
        );
        if (idx === -1) {
          // Resolution ahead of its pending (eviction truncated the range).
          // Record an inert tombstone so the pending, when it replays, upserts
          // into an ALREADY-resolved entry instead of resurrecting the ask.
          next = [
            ...next,
            {
              kind: "agent_ask",
              id: `${eventId(event)}#${askId}`,
              askId,
              issueIid: event.issueIid,
              field: null,
              question: "",
              resolved: true,
              resolvedReason: event.reason,
            },
          ];
          continue;
        }
        const existing = next[idx]!;
        if (existing.kind !== "agent_ask" || existing.resolved) continue;
        next = [
          ...next.slice(0, idx),
          { ...existing, resolved: true, resolvedReason: event.reason },
          ...next.slice(idx + 1),
        ];
      }
      return next;
    }
    case "readiness_breakdown_ready": {
      // Replaced in place per issue (an adjust → re-propose supersedes the
      // prior proposal); the token rotates with each valid proposal.
      const entry: TimelineEntry = {
        kind: "breakdown_card",
        id: eventId(event),
        issueIid: event.issueIid,
        noSplit: event.noSplit,
        rationale: event.rationale,
        criteria: event.criteria,
        children: event.children,
        ...(event.parked !== undefined ? { parked: event.parked } : {}),
        ...(event.breakdownToken !== undefined
          ? { breakdownToken: event.breakdownToken }
          : {}),
        ...(event.childrenHash !== undefined
          ? { childrenHash: event.childrenHash }
          : {}),
      };
      const idx = timeline.findIndex(
        (e) => e.kind === "breakdown_card" && e.issueIid === event.issueIid,
      );
      if (idx === -1) return [...timeline, entry];
      return [...timeline.slice(0, idx), entry, ...timeline.slice(idx + 1)];
    }
    case "bridge_stream": {
      // PRIVACY: a post-green tail turn (plan / sizing / breakdown) narrates
      // over full repo paths; the server stamps its stream events with
      // `era: "tail"` and NO client surface may render them as chat prose.
      // (Focus RefineFlow has its own preplanIdsRef belt; this covers every
      // other consumer, e.g. IssuePage's embedded chat.)
      if (event.era === "tail") return timeline;
      // Structured stream-json from the bridge's `claude` runner. Two
      // shapes carry assistant text:
      //   1. whole-message `{ type: "assistant", message: { content: [...] } }`
      //      — what `claude --output-format stream-json --verbose` emits
      //      without `--include-partial-messages`. One event per Claude
      //      message; we push a fresh assistant_turn per event (agentic
      //      turns interleave with tool_use, so folding would merge
      //      logically distinct messages).
      //   2. partial `content_block_delta` text_delta — only appears if
      //      the bridge ever passes `--include-partial-messages`. We
      //      fold consecutive deltas into the in-progress turn.
      // Raw stdout/stderr chunks are logs, not chat content, and
      // intentionally fall through.
      if ("payload" in event && event.payload !== undefined) {
        const p = event.payload as Record<string, unknown> | null;
        if (p !== null && typeof p === "object") {
          if (p.type === "assistant") {
            // A SUBAGENT's message (the Task tool's research agent) streams on
            // the same lane, tagged with the tool call it runs under. It is the
            // agent's own working notes, never a reply: live on dev a search
            // report ("Conclusion: this is a generic/test-fixture issue …")
            // rendered as a Loom message in the middle of a German refine.
            if (isSubagentMessage(p)) return timeline;
            const message = p.message as
              | { content?: unknown }
              | null
              | undefined;
            const content = message?.content;
            if (Array.isArray(content)) {
              const text = content
                .filter(
                  (b): b is { type: "text"; text: string } =>
                    typeof b === "object" &&
                    b !== null &&
                    (b as { type?: unknown }).type === "text" &&
                    typeof (b as { text?: unknown }).text === "string",
                )
                .map((b) => b.text)
                .join("");
              if (text.length > 0 && !isNoReplyNotice(text)) {
                return [
                  ...timeline,
                  { kind: "assistant_turn", id: eventId(event), text },
                ];
              }
            }
            return timeline;
          }
          if (
            p.type === "content_block_delta" &&
            typeof (p.delta as { text?: unknown } | undefined)?.text === "string"
          ) {
            const text = (p.delta as { text: string }).text;
            const last = timeline[timeline.length - 1];
            if (last !== undefined && last.kind === "assistant_turn") {
              const updated: TimelineEntry = {
                kind: "assistant_turn",
                id: last.id,
                text: last.text + text,
              };
              return [...timeline.slice(0, -1), updated];
            }
            return [
              ...timeline,
              { kind: "assistant_turn", id: eventId(event), text },
            ];
          }
        }
      }
      return timeline;
    }
    case "mode_changed":
      // Mode is exposed via the hook's `mode` field, not the timeline.
      return timeline;
    case "coding_progress":
      // Phase 6 — file_touched / commit / bash / assistant_text markers
      // surfaced as inline activity in the chat timeline. The PM agent
      // never sees these; they are purely a visual signal that something
      // is happening inside the coding bridge. assistant_text is filtered
      // out here because the bridge_stream content_block_delta path
      // already renders the streaming assistant text — duplicating would
      // double-render the same content.
      if (event.entry.kind === "assistant_text") return timeline;
      // Tail-era activity carries repo paths (file_touched) — suppress like
      // the era-tagged assistant stream above.
      if (event.era === "tail") return timeline;
      return [
        ...timeline,
        {
          kind: "coding_progress",
          id: eventId(event),
          entry: event.entry,
        },
      ];
    case "user_message":
      return [
        ...timeline,
        {
          kind: "user_message",
          id: eventId(event),
          text: event.text,
          ...(typeof event.issueIid === "number"
            ? { issueIid: event.issueIid }
            : {}),
          ...(typeof event.clientMsgId === "string"
            ? { clientMsgId: event.clientMsgId }
            : {}),
        },
      ];
    default:
      return timeline;
  }
};

/**
 * How long the attach burst must be quiet before `busyKnown` turns true. The
 * server writes the snapshot and the whole replay in one synchronous loop, so
 * inside the burst frames arrive back to back; a gap this long means it is over.
 */
export const ATTACH_SETTLE_QUIET_MS = 400;
/** The latest `busyKnown` turns true after the snapshot, however busy the
 *  socket stays — a live turn streams frames every second or so. */
export const ATTACH_SETTLE_MAX_MS = 4_000;

const INITIAL_BRIDGE: BridgeStatusState = { state: "pending" };
const INITIAL_BRANCH: BranchState = { commits: 0, summary: null };

// Human-idle kill. After this long with no human gesture we WARN (a countdown
// dialog); when the countdown elapses we hard-DELETE the session, which makes
// the server cancel the per-session CI job and free the GitLab runner. This is
// the ONLY thing short of GitLab's 60-min job ceiling that reclaims the sandbox:
// the 25s WS heartbeat keeps `markUsed` fresh, so the server-side idle reaper
// never fires while a tab stays open. "Idle" here means no *human* activity, not
// a dead socket — an attended-but-untouched tab is exactly the burn we kill.
const IDLE_WARN_MS = 15 * 60 * 1000; // no human activity → show countdown
const IDLE_KILL_COUNTDOWN_MS = 2 * 60 * 1000; // visible grace before hard kill
// User gestures that count as "still here": they re-arm the warn timer and, once
// the countdown is showing, cancel the pending kill (the user came back).
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "wheel",
] as const;

/**
 * One queued client frame awaiting delivery confirmation. Against an
 * ack-capable server an entry is released ONLY by the server's `ack` control —
 * "handed to an OPEN socket" is explicitly NOT delivery (the Cloud Run timeout
 * kills the socket silently and `readyState` stays OPEN; the refine
 * answer-loss root cause). Against an old server (no `acks` capability) the
 * entry is released when the write reached an OPEN socket, replicating the
 * legacy outbox.
 */
interface OutboxEntry {
  readonly clientMsgId: string;
  readonly frame: ClientFrame;
  readonly kind: ClientFrame["kind"];
  readonly enqueuedAt: number;
  /**
   * Opaque send-scope binding (e.g. `refine:42`). A scoped entry is dropped at
   * redelivery time when the active scope has moved on — a held refine answer
   * redelivered after the user switched issues would otherwise auto-ground and
   * green the WRONG issue's card (the cross-issue pollution hazard).
   */
  readonly scope?: string;
}

/**
 * Redelivery cutoff. An entry that has waited this long is stale in every flow
 * we have (refine answers, chat prompts) — delivering it into a context that
 * has moved on does more harm than the loss. Dropped loudly (wsDiag +
 * console.warn), never silently.
 */
const OUTBOX_TTL_MS = 15 * 60 * 1000;

/** Bound on the acked-ids map handed to consumers (RefineFlow only ever needs
 *  the most recent flush). Oldest-first eviction; Map preserves insertion order. */
const ACKED_IDS_CAP = 200;

const mintMsgId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `m${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/** Clone a frame with the delivery id stamped into its payload. `ping` never
 *  carries one (it has no payload and needs no delivery guarantee). */
const withMsgId = (frame: ClientFrame, clientMsgId: string): ClientFrame =>
  frame.kind === "ping"
    ? frame
    : ({ ...frame, payload: { ...frame.payload, clientMsgId } } as ClientFrame);

export const useWsSession = (sessionId: string | null): UseWsSession => {
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [status, setStatus] = useState<UseWsSession["status"]>("connecting");
  const [reauth, setReauth] = useState<ReauthState>({
    required: false,
    gitlabHost: "",
  });
  const [bridge, setBridge] = useState<BridgeStatusState>(INITIAL_BRIDGE);
  const [sandbox, setSandbox] = useState<SandboxStatusSnapshot>(
    INITIAL_SANDBOX_STATUS,
  );
  const [mode, setMode] = useState<SessionMode>("pm");
  const [branch, setBranch] = useState<BranchState>(INITIAL_BRANCH);
  const [agentBusy, setAgentBusy] = useState(false);
  const [busyKnown, setBusyKnown] = useState(false);
  const [agentWork, setAgentWork] = useState<AgentWorkState>(INITIAL_AGENT_WORK);
  // The attach burst: set when this socket's `agent_turn` snapshot lands while
  // busy is not yet known, cleared when `busyKnown` flips. Every event that
  // arrives meanwhile re-arms the settle timer (see `busyKnown`).
  const attachBurstRef = useRef<{ readonly startedAt: number } | null>(null);
  const attachSettleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const busyKnownRef = useRef(false);
  busyKnownRef.current = busyKnown;
  const armAttachSettle = useCallback((): void => {
    const burst = attachBurstRef.current;
    if (burst === null) return;
    if (attachSettleTimerRef.current !== null) clearTimeout(attachSettleTimerRef.current);
    const wait = Math.max(
      0,
      Math.min(ATTACH_SETTLE_QUIET_MS, burst.startedAt + ATTACH_SETTLE_MAX_MS - Date.now()),
    );
    attachSettleTimerRef.current = setTimeout(() => {
      attachSettleTimerRef.current = null;
      if (attachBurstRef.current !== burst) return;
      attachBurstRef.current = null;
      busyKnownRef.current = true;
      setBusyKnown(true);
    }, wait);
  }, []);
  // Read inside the idle timeout without re-arming the listeners every turn —
  // we never cut the socket mid-turn (events would be missed until reconnect).
  const agentBusyRef = useRef(false);
  agentBusyRef.current = agentBusy;
  // Latest deterministic readiness-card apply outcome (server-side direct
  // apply). The refine surface reads this to leave the "applying…" state for a
  // terminal "applied" or a retryable error, instead of hanging when the agent
  // that would otherwise apply is gone (instance recycle / eviction).
  const [applyResult, setApplyResult] = useState<{
    readonly issueIid: number;
    readonly ok: boolean;
    readonly webUrl?: string;
    readonly error?: string;
    readonly code?: string;
    readonly planSectionWritten?: boolean;
    readonly planDropReason?: string;
  } | null>(null);
  // Latest implementation-plan tail-round outcome (end of refine). `ready`
  // carries the non-technical business summary for the validation step;
  // `unavailable` tells the surface to fall through to Accept.
  const [planResult, setPlanResult] = useState<UseWsSession["planResult"]>(null);
  // Sizing + breakdown tail-step outcomes (see the interface docs). All three
  // rebuild from the full replay, so a reconnect can't strand a surface.
  const [sizingResult, setSizingResult] =
    useState<UseWsSession["sizingResult"]>(null);
  const [breakdownUnavailable, setBreakdownUnavailable] =
    useState<UseWsSession["breakdownUnavailable"]>(null);
  const [breakdownProgress, setBreakdownProgress] =
    useState<UseWsSession["breakdownProgress"]>(null);
  const [breakdownResult, setBreakdownResult] =
    useState<UseWsSession["breakdownResult"]>(null);
  const [breakdownOpResult, setBreakdownOpResult] =
    useState<UseWsSession["breakdownOpResult"]>(null);
  // Figma link chips, folded from `figma_link_state`. Wiped and rebuilt on
  // every attach (see the attach effect) — the full replay re-feeds every row,
  // so this can never carry another session's links or a stale decline.
  const [figmaLinks, setFigmaLinks] =
    useState<ReadonlyArray<FigmaLinkSnapshot>>(EMPTY_FIGMA_LINKS);
  // The pictures this chat had, folded from `figma_scene`, keyed by `previewId`
  // AND by every `linkId` the read was about. Wiped and rebuilt on every attach
  // exactly like `figmaLinks` — the full replay re-feeds every scene, and a map
  // carried across a session switch would draw another session's picture under
  // a handle this one happens to reuse.
  const [figmaScenes, setFigmaScenes] =
    useState<ReadonlyMap<string, FigmaSceneSnapshot>>(EMPTY_FIGMA_SCENES);
  // Latest autonomous-implement pipeline frame (the roadmap the ImplementFlow
  // surface renders). Null until the first `implement_stage` event.
  const [implement, setImplement] = useState<ImplementSnapshot | null>(null);
  const [reconnectKey, setReconnectKey] = useState(0);
  // Bumped by resetSession() — see its doc comment.
  const [resetEpoch, setResetEpoch] = useState(0);
  // Remount-safe UI state for the timeline subtree (see the interface doc).
  // Ref-held so it survives every reconnect; cleared when the session itself
  // changes so a new session can't restore another session's drafts/latches.
  const uiStateRef = useRef<RemountSafeStore | null>(null);
  if (uiStateRef.current === null) uiStateRef.current = createRemountSafeStore();
  const uiState = uiStateRef.current;
  useEffect(() => {
    return () => uiState.clear();
  }, [sessionId, uiState]);
  // Human-idle kill. `idleCountdownEndsAt` is the wall-clock deadline shown in
  // the warning dialog (null when not counting down); `idleKilled` latches once
  // the countdown elapsed and we issued the hard DELETE. The socket is left OPEN
  // during the countdown so any gesture cancels cleanly; only `idleKilled` keeps
  // the attach effect shut.
  const [idleCountdownEndsAt, setIdleCountdownEndsAt] = useState<number | null>(
    null,
  );
  const [idleKilled, setIdleKilled] = useState(false);
  // Read inside the idle watchdog without re-arming on every implement frame: an
  // active autonomous run must never be killed (the server also refuses to
  // hard-DELETE a protected run, so a countdown would lie to the user).
  const implementActiveRef = useRef(false);
  implementActiveRef.current = implement?.status === "active";
  const clientRef = useRef<WsClient | null>(null);
  // Outbox of frames awaiting delivery confirmation (see OutboxEntry). Lives
  // in the hook so it survives reconnects; cleared on sessionId change and on
  // resetSession (a pre-reset answer must not redeliver into the post-reset
  // session). Against an ack-capable server the entries double as the
  // re-send-on-reconnect queue — the server's ingress dedupe (clientMsgId)
  // makes redelivery safe even when the original DID arrive.
  const outboxRef = useRef<OutboxEntry[]>([]);
  // Sticky capability latch: the server advertised `acks` on session_attached.
  // Per-session (cleared on sessionId change), survives reconnects — a
  // mid-session server downgrade leaves entries to the TTL drop rather than
  // re-sending forever.
  const acksEnabledRef = useRef(false);
  // The same shape of sticky, per-session capability latch, for the design
  // sketch. STATE and not a ref: it gates an affordance the chat chips render,
  // so the surfaces have to re-render when it lands. Cleared on a session
  // change beside `acksEnabledRef` for the reason stated there — the latch may
  // differ per server revision.
  const [figmaPreviewEnabled, setFigmaPreviewEnabled] = useState(false);
  // Id of the `budget_exhausted` entry the user extended past, if any. State
  // (not a ref) so clearing it re-renders the flow back onto its real screen.
  const [extendedBudgetEntryId, setExtendedBudgetEntryId] = useState<
    string | null
  >(null);
  // clientMsgId → send-attempt wall clock, so the client-trace `frame_ack` can
  // carry a real round-trip. Bounded by the outbox's own TTL sweep below; an
  // entry that never acks is swept with its outbox entry.
  const sendAttemptAtRef = useRef<Map<string, number>>(new Map());
  // Active send-scope (see OutboxEntry.scope). Owned by whichever surface is
  // currently sending scope-bound messages (RefineFlow sets `refine:<iid>`).
  const activeSendScopeRef = useRef<string | null>(null);
  // clientMsgId → bus seq of the user_message echo (null for seq-less acks).
  // State (not a ref) so consumers — RefineFlow's seq-keyed flush
  // suppression — re-render when the ack lands.
  const [ackedIds, setAckedIds] = useState<ReadonlyMap<string, number | null>>(
    () => new Map(),
  );
  // Detect a genuine sessionId change inside the attach effect: outbox + acks
  // belong to ONE session; carrying them across re-targets a queued message at
  // a different conversation.
  const attachedSessionIdRef = useRef<string | null>(null);
  // Auto-reconnect bookkeeping. The dev infra (Cloud Run) abruptly drops long
  // or idle WebSockets with a 1006 close every few minutes; without automatic
  // reconnect the user has to click "reconnect" each time AND a turn-end event
  // that lands during the gap is missed. `deliberateCloseRef` distinguishes our
  // own teardown (unmount / manual reconnect) from a real drop; `expiredRef`
  // and `deniedRef` stop us from reconnecting a session the server has
  // terminally refused (gone / not ours).
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deliberateCloseRef = useRef(false);
  const expiredRef = useRef(false);
  const deniedRef = useRef(false);

  // MINOR-2 instrumentation: the open streamed run, so one turn logs ONE entry
  // rather than one per chunk. Deletable in one commit once the duplicate
  // assistant turn is attributed.
  const deltaRunRef = useRef<{ chars: number; seq: number } | null>(null);

  // The WS-attach effect intentionally depends only on `sessionId` /
  // `reconnectKey`, so it can't list `t` without re-opening the socket on a
  // locale switch. Keep the latest translator in a ref the long-lived
  // callbacks read for the (diagnostic) error-frame labels.
  const t = useT();
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    if (sessionId === null) {
      // Inert hook — open no socket. Used by ChatPage when an
      // `externalSession` is supplied (IssuePage owns the single live
      // session and threads it in), so the hook can still be called
      // unconditionally — rules of hooks — without opening a second
      // WebSocket onto the same session. Reset to the pristine state in
      // case we transitioned here from a real id.
      setStatus("closed");
      setBridge(INITIAL_BRIDGE);
      setSandbox(INITIAL_SANDBOX_STATUS);
      setMode("pm");
      setBranch(INITIAL_BRANCH);
      setAgentBusy(false);
      setBusyKnown(false);
      setAgentWork(INITIAL_AGENT_WORK);
      busyKnownRef.current = false;
      attachBurstRef.current = null;
      if (attachSettleTimerRef.current !== null) {
        clearTimeout(attachSettleTimerRef.current);
        attachSettleTimerRef.current = null;
      }
      setTimeline([]);
      setFigmaLinks(EMPTY_FIGMA_LINKS);
      setFigmaScenes(EMPTY_FIGMA_SCENES);
      setSizingResult(null);
      setBreakdownUnavailable(null);
      setBreakdownProgress(null);
      setBreakdownResult(null);
      setBreakdownOpResult(null);
      setIdleCountdownEndsAt(null);
      setIdleKilled(false);
      clientRef.current = null;
      outboxRef.current = [];
      acksEnabledRef.current = false;
      setFigmaPreviewEnabled(false);
      sendAttemptAtRef.current.clear();
      setClientTraceSession(null);
      setAckedIds(new Map());
      attachedSessionIdRef.current = null;
      return;
    }
    if (attachedSessionIdRef.current !== sessionId) {
      // Genuine session switch (sidebar click, reset → new id, deep link):
      // queued frames and ack bookkeeping belong to the PREVIOUS session —
      // draining them into the new one would replay another conversation's
      // messages (and the capability latch may differ per server revision).
      if (outboxRef.current.length > 0) {
        wsDiag("outbox_dropped_session_change", {
          sessionId,
          prior: attachedSessionIdRef.current,
          count: outboxRef.current.length,
        });
        for (const entry of outboxRef.current) {
          reportClientEvent("frame_dropped", {
            kind: entry.kind,
            why: "session_change",
          });
        }
      }
      outboxRef.current = [];
      acksEnabledRef.current = false;
      setFigmaPreviewEnabled(false);
      sendAttemptAtRef.current.clear();
      setAckedIds(new Map());
      attachedSessionIdRef.current = sessionId;
      // Point the reporter at the new session AFTER the previous session's
      // drops are queued, so `setClientTraceSession`'s flush ships them under
      // the id they actually belong to.
      setClientTraceSession(sessionId);
      // The terminal refusals belong to the PREVIOUS session too. Without this
      // reset they latch for the hook's lifetime, so the NEXT session opened in
      // the same tab — the exact path a re-approved user takes, and the one the
      // surfaces' self-heal steers them onto — would never auto-reconnect from
      // an ordinary 1006.
      expiredRef.current = false;
      deniedRef.current = false;
      // The idle kill belongs to the PREVIOUS session, not this hook instance.
      // Without this reset the latch survives any non-null → non-null switch
      // (browser Back between /chat ids, a project-session re-adopt) and the
      // killed branch below pins the NEW, perfectly-alive session at
      // "idle_killed" without ever opening its socket — recoverable only by a
      // full reload. Return WITHOUT attaching: the latch write re-runs this
      // effect (idleKilled is a dep), and that run does the one clean attach —
      // attaching here too would open a second socket the re-run's cleanup
      // immediately churns through.
      if (idleKilled) {
        setIdleKilled(false);
        setIdleCountdownEndsAt(null);
        return;
      }
    }
    if (idleKilled) {
      // Hard-killed for inactivity: the countdown elapsed, we issued the hard
      // DELETE, and the server has cancelled the CI job. The previous effect
      // run's cleanup shut the socket (deliberateCloseRef → no auto-reconnect);
      // we keep the timeline visible.
      //
      // The SESSION survives. `?reason=idle` calls `liveRegistry.remove()` and
      // never `session.end()` (`http/sessions.ts:668-677`), so `endedAt` stays
      // unstamped: the sandbox and its pipeline are gone, the session is not,
      // and `POST /sessions/:id/reconnect` re-hydrates it in place. Nothing is
      // said here about a fresh session because none is needed.
      //
      // The phase must go with it. This return precedes the
      // `setSandbox(INITIAL_SANDBOX_STATUS)` below, so without this write the
      // snapshot keeps whatever it last was — a green "ready" pill over a
      // cancelled CI job. `suspended` is never emitted by the server, so the
      // value is unambiguous: it means exactly this.
      wsDiag("idle_killed", { sessionId });
      setSandbox({ phase: "suspended" });
      setStatus("idle_killed");
      return;
    }
    deliberateCloseRef.current = false;
    wsDiag("attach_effect", {
      sessionId,
      reconnectKey,
      attempt: reconnectAttemptRef.current,
    });
    setStatus("connecting");
    setBridge(INITIAL_BRIDGE);
    setSandbox(INITIAL_SANDBOX_STATUS);
    setMode("pm");
    setBranch(INITIAL_BRANCH);
    // …but it is no longer KNOWN until this socket's snapshot lands and the
    // replay behind it has.
    setBusyKnown(false);
    busyKnownRef.current = false;
    attachBurstRef.current = null;
    if (attachSettleTimerRef.current !== null) {
      clearTimeout(attachSettleTimerRef.current);
      attachSettleTimerRef.current = null;
    }
    // agentBusy deliberately NOT reset here: the server snapshots agent_turn
    // (busy OR idle) on every attach, so the pre-drop value carrying through
    // the replay window is strictly more accurate than a hard `false` — and a
    // transient false mid-wipe let the just-flushed-question suppression in
    // RefineFlow lapse, re-pinning a question the user had already answered.
    // Reset the timeline when sessionId changes so navigating from one
    // session to another (sidebar click, reset → new id, deep link)
    // doesn't carry the prior session's messages into the new one. The
    // newly-attaching WS replays the new session's bus.events from
    // scratch.
    setTimeline([]);
    // Figma chips go with the timeline: same replay, same wipe. Keeping them
    // across the wipe would strand a `loading` chip whose completion event the
    // replay is about to re-deliver anyway.
    setFigmaLinks(EMPTY_FIGMA_LINKS);
    // The pictures go with the chips, for the same reason: the replay re-feeds
    // every `figma_scene`, and a map that outlived the wipe would answer
    // `figmaSceneFor` with a scene from the session the user just left.
    setFigmaScenes(EMPTY_FIGMA_SCENES);
    const client = openWsSession(sessionId, {
      onOpen: () => {
        // NOTE: the backoff reset does NOT belong here. Fastify completes the
        // WS upgrade before the route handler runs, so `open` fires even for a
        // socket the server is about to reject — resetting on it pinned the
        // delay at 500 * 2^0 forever and turned any close-on-attach into a
        // ~2 req/s storm. It lives on `session_attached` instead, the first
        // frame past every server-side gate. See that branch in `onControl`.
        setStatus("open");
        // Redeliver every entry still awaiting confirmation — typed while
        // disconnected, written into a dead-but-OPEN socket, or sent but never
        // acked. Runs BEFORE any new user send on this socket (onOpen is
        // synchronous), preserving message order. The server's ingress dedupe
        // makes re-sending an already-arrived frame a no-op + re-ack.
        const now = Date.now();
        const pending = outboxRef.current.filter((entry) => {
          if (now - entry.enqueuedAt > OUTBOX_TTL_MS) {
            wsDiag("outbox_expired", {
              sessionId,
              clientMsgId: entry.clientMsgId,
              kind: entry.kind,
              ageMs: now - entry.enqueuedAt,
            });
            console.warn(
              `[ws] dropping ${entry.kind} older than ${OUTBOX_TTL_MS / 60000}m from outbox (never confirmed delivered)`,
            );
            sendAttemptAtRef.current.delete(entry.clientMsgId);
            reportClientEvent("frame_dropped", {
              kind: entry.kind,
              why: "expired",
            });
            return false;
          }
          if (
            entry.scope !== undefined &&
            entry.scope !== activeSendScopeRef.current
          ) {
            // The surface that owned this message has moved on (e.g. refine
            // switched issues) — delivering it now would ground against the
            // wrong context. Loud drop.
            wsDiag("outbox_scope_dropped", {
              sessionId,
              clientMsgId: entry.clientMsgId,
              kind: entry.kind,
              scope: entry.scope,
              active: activeSendScopeRef.current,
            });
            sendAttemptAtRef.current.delete(entry.clientMsgId);
            reportClientEvent("frame_dropped", {
              kind: entry.kind,
              why: "scope",
            });
            return false;
          }
          return true;
        });
        outboxRef.current = [];
        if (pending.length > 0) {
          wsDiag("outbox_flush", { sessionId, count: pending.length });
        }
        for (const entry of pending) {
          const delivered = clientRef.current?.send(entry.frame) ?? false;
          reportClientEvent("frame_retry", { kind: entry.kind, delivered });
          if (delivered && !acksEnabledRef.current) {
            // Legacy server: no ack will ever come — OPEN-handed is the best
            // confirmation available (pre-ack behaviour).
            continue;
          }
          // Ack-capable: hold until the ack control releases it. Undelivered
          // (socket flapped mid-drain): hold for the next open either way.
          outboxRef.current.push(entry);
        }
        if (outboxRef.current.length > 0) {
          wsDiag("outbox_requeued", {
            sessionId,
            count: outboxRef.current.length,
          });
        }
      },
      onEvent: (event) => {
        // The attach burst: every frame that lands between the snapshot and
        // the end of the replay pushes `busyKnown` back (see its doc).
        if (attachBurstRef.current !== null) armAttachSettle();
        // MINOR-2 (a chat answer occasionally rendered twice), instrumented
        // rather than fixed: the two assistant lanes behave differently and
        // nobody has confirmed which one duplicates. The servant path FOLDS
        // into the preceding `assistant_turn`; the bridge whole-message path
        // ALWAYS appends a fresh entry with a new id and compares nothing. A
        // recurrence looks like two adjacent `assistant_text` entries with
        // different `lane` and equal `chars` around a handoff — `dumpWsDiag()`.
        //
        // Two separate mechanisms, neither of which does the other's job.
        // `quiet` (third parameter, defaults to false) suppresses ONLY the
        // `console.info` mirror — `wsDiag` pushes to the ring buffer and applies
        // its 400-entry splice before it ever looks at the flag, so quiet costs
        // the buffer exactly as much as loud. What keeps the buffer from being
        // flooded is the delta-RUN accumulator below: one entry per streamed
        // turn instead of one per chunk. Client replay is full on every
        // reconnect, so per-chunk entries would evict exactly the
        // `session_expired` / `frame_send` entries this work wants to read.
        // Keep both.
        //
        // The authoritative discriminator is still `app.session_log`: TWO
        // assistant-bearing events around the handoff means the SERVER sent the
        // text twice (fix belongs in `runHandoff`'s `<session-context>` seed);
        // ONE event rendered as two rows means the client duplicated it (fix
        // belongs in `applyEvent`).
        //
        // A trailing run stays unflushed until the next event — normally the
        // turn-end event, immediately.
        const flushDeltaRun = (): void => {
          const run = deltaRunRef.current;
          if (run === null) return;
          deltaRunRef.current = null;
          wsDiag("assistant_text", { lane: "delta", ...run }, true);
        };
        const arrival = assistantTextArrival(event);
        if (arrival === null) {
          flushDeltaRun();
        } else if (arrival.lane === "delta") {
          const run = deltaRunRef.current;
          deltaRunRef.current =
            run === null
              ? { chars: arrival.chars, seq: event.seq }
              : { chars: run.chars + arrival.chars, seq: run.seq };
        } else {
          flushDeltaRun();
          wsDiag(
            "assistant_text",
            { lane: "whole", chars: arrival.chars, seq: event.seq },
            true,
          );
        }
        // What the server says about the agent's turn — before the branches
        // below, several of which return. See `./agent-work`.
        if (touchesAgentWork(event)) {
          const arrival = Date.now();
          setAgentWork((prev) => foldAgentWork(prev, event, arrival));
        }
        if (event.type === "reauth_required") {
          setReauth({ required: true, gitlabHost: event.gitlabHost });
          return;
        }
        if (event.type === "bridge_status") {
          setBridge({
            state: event.state,
            ...(event.pipelineId !== undefined ? { pipelineId: event.pipelineId } : {}),
            ...(event.etaSeconds !== undefined ? { etaSeconds: event.etaSeconds } : {}),
            ...(event.message !== undefined ? { message: event.message } : {}),
          });
          return;
        }
        if (event.type === "sandbox_status") {
          const arrival = Date.now();
          setSandbox((prev) => ({
            phase: event.phase,
            ...(event.pipelineId !== undefined
              ? { pipelineId: event.pipelineId }
              : {}),
            ...(event.message !== undefined ? { message: event.message } : {}),
            sinceMs: phaseSinceClientMs(event, prev, arrival),
          }));
          return;
        }
        if (isFigmaSceneEvent(event)) {
          // Lane B like the chips, and ahead of `applyEvent` for the same
          // reason: a scene is not a transcript row. `applyEvent`'s `default`
          // would return the timeline untouched anyway — this branch returns so
          // the intent is stated rather than relied on.
          setFigmaScenes((prev) => applyFigmaSceneEvent(prev, event));
          return;
        }
        if (event.type === "figma_link_state") {
          // Lane B, not the timeline: a chip is a live status the composer and
          // the transcript both read, and it must be addressable by `linkId`
          // rather than by position. Folded as an UPSERT-PATCH per row — the
          // replay re-feeds every event, so appending would stack the same link
          // once per reconnect (the `question_pending` lesson).
          const rows = event.links;
          if (rows.length > 0) {
            setFigmaLinks((prev) => applyFigmaLinkRows(prev, rows, event.seq));
          }
          return;
        }
        if (event.type === "mode_changed") {
          setMode(event.to);
          // fall through so applyEvent gets a chance to log it (no-op today)
        }
        if (event.type === "coding_branch_state") {
          setBranch({ commits: event.commits, summary: event.summary });
          return;
        }
        if (event.type === "agent_turn") {
          setAgentBusy(event.state === "busy");
          // The attach snapshot opens the burst; a live edge on a settled
          // socket is known the moment it lands.
          if (!busyKnownRef.current && attachBurstRef.current === null) {
            attachBurstRef.current = { startedAt: Date.now() };
            armAttachSettle();
          }
          return;
        }
        if (event.type === "readiness_apply_result") {
          setApplyResult({
            issueIid: event.issueIid,
            ok: event.ok,
            ...(event.webUrl !== undefined ? { webUrl: event.webUrl } : {}),
            ...(event.error !== undefined ? { error: event.error } : {}),
            ...(event.code !== undefined ? { code: event.code } : {}),
            ...(event.planSectionWritten !== undefined
              ? { planSectionWritten: event.planSectionWritten }
              : {}),
            ...(event.planDropReason !== undefined
              ? { planDropReason: event.planDropReason }
              : {}),
          });
          return;
        }
        if (event.type === "readiness_plan_ready") {
          setPlanResult({
            kind: "ready",
            issueIid: event.issueIid,
            businessSummary: event.businessSummary,
            ...(event.planRevision !== undefined
              ? { planRevision: event.planRevision }
              : {}),
          });
          return;
        }
        if (event.type === "readiness_plan_unavailable") {
          setPlanResult({
            kind: "unavailable",
            issueIid: event.issueIid,
            reason: event.reason,
          });
          return;
        }
        if (event.type === "readiness_sizing_ready") {
          setSizingResult({
            kind: "ready",
            issueIid: event.issueIid,
            sizeSummary: event.sizeSummary,
            overall: event.overall,
            risks: event.risks,
            criteriaCount: event.criteriaCount,
            splitRecommended: event.splitRecommended,
            oversizedSingleCriterion: event.oversizedSingleCriterion === true,
            verdictStale: event.verdictStale === true,
          });
          return;
        }
        if (event.type === "readiness_sizing_unavailable") {
          setSizingResult({
            kind: "unavailable",
            issueIid: event.issueIid,
            reason: event.reason,
          });
          return;
        }
        if (event.type === "readiness_breakdown_unavailable") {
          setBreakdownUnavailable({
            issueIid: event.issueIid,
            reason: event.reason,
          });
          return;
        }
        if (event.type === "readiness_breakdown_op_result") {
          setBreakdownOpResult({
            issueIid: event.issueIid,
            ok: event.ok,
            ...(event.error !== undefined ? { error: event.error } : {}),
            ...(event.message !== undefined ? { message: event.message } : {}),
          });
          return;
        }
        if (event.type === "readiness_breakdown_ready") {
          // A landed proposal supersedes any prior degrade — one transient
          // unavailable must not lock the split out for the session. Falls
          // through: the proposal itself is a timeline entry (applyEvent).
          setBreakdownUnavailable(null);
          // Deliberately does NOT clear `breakdownOpResult`. The stale and
          // refused paths emit the refusal and then re-emit the current card in
          // the same breath, so clearing here would erase the explanation
          // roughly a millisecond after it appeared. The banner is cleared when
          // the user tries again (see `sendBreakdownOp`), which is the only
          // moment it has actually been superseded.
        }
        if (event.type === "readiness_breakdown_progress") {
          setBreakdownProgress((prev) => {
            const base =
              prev !== null && prev.issueIid === event.issueIid
                ? prev
                : {
                    issueIid: event.issueIid,
                    total: event.total,
                    createdByLocalId: new Map<
                      string,
                      { iid: number; title: string }
                    >(),
                    linkedLocalIds: new Set<string>(),
                  };
            const createdByLocalId = new Map(base.createdByLocalId);
            const linkedLocalIds = new Set(base.linkedLocalIds);
            if (event.step === "created") {
              createdByLocalId.set(event.localId, {
                iid: event.childIid,
                title: event.childTitle,
              });
            } else {
              linkedLocalIds.add(event.localId);
            }
            return {
              issueIid: event.issueIid,
              total: event.total,
              createdByLocalId,
              linkedLocalIds,
            };
          });
          return;
        }
        if (event.type === "readiness_breakdown_result") {
          setBreakdownResult({
            issueIid: event.issueIid,
            ok: event.ok,
            ...(event.children !== undefined ? { children: event.children } : {}),
            ...(event.error !== undefined ? { error: event.error } : {}),
            ...(event.progress !== undefined ? { progress: event.progress } : {}),
          });
          return;
        }
        if (event.type === "implement_stage") {
          // Merge: later frames (deploy/done) often omit mrUrl/environments
          // that an earlier frame carried — keep the sticky fields.
          setImplement((prev) => ({
            issueIid: event.issueIid,
            stage: event.stage,
            status: event.status,
            detail: event.detail ?? null,
            mrUrl: event.mrUrl ?? prev?.mrUrl ?? null,
            environments:
              event.environments ?? prev?.environments ?? [],
            dorBlocked: event.dorBlocked ?? null,
          }));
          return;
        }
        setTimeline((prev) => applyEvent(prev, event));
      },
      onControl: (control: ServerControl) => {
        if (control.kind === "session_attached") {
          // THE proof this socket is healthy: every terminal gate in the
          // server's stream handler (cookie, approval, rehydrate, ownership)
          // closes BEFORE this frame is sent, so reaching it means the server
          // accepted us. Only now is the exponential backoff safe to clear —
          // resetting on the bare `open` event let a rejected socket restart
          // the ladder on every cycle, which is what made a refused session
          // reconnect twice a second indefinitely.
          reconnectAttemptRef.current = 0;
          if (control.acks === true) {
            // Sticky for the session: from here on, outbox entries are held
            // until the server's ack — not released on OPEN-handed writes.
            acksEnabledRef.current = true;
          }
          // Sticky the same way, and DOWN-only within a session is not a case
          // that exists: the route is registered at boot, so a reconnect to the
          // same deployment answers the same way. A reconnect to an older one
          // simply omits it, and `setFigmaPreviewEnabled` then leaves the latch
          // where it is rather than flapping the affordance mid-session.
          if (control.figmaPreview === true) setFigmaPreviewEnabled(true);
        } else if (control.kind === "ack") {
          // Delivery confirmed at the server's ingress — release the entry and
          // publish the echo seq for seq-keyed consumers (RefineFlow's flush
          // suppression). A duplicate redelivery re-acks with the original
          // seq, so this is idempotent.
          const before = outboxRef.current.length;
          outboxRef.current = outboxRef.current.filter(
            (e) => e.clientMsgId !== control.clientMsgId,
          );
          wsDiag("ack", {
            sessionId,
            clientMsgId: control.clientMsgId,
            ...(control.seq !== undefined ? { seq: control.seq } : {}),
            released: before - outboxRef.current.length,
          });
          // Pairs with the `frame_send` above. A send with no matching ack is
          // the signature of a frame lost in flight.
          const sentAt = sendAttemptAtRef.current.get(control.clientMsgId);
          if (sentAt !== undefined) {
            sendAttemptAtRef.current.delete(control.clientMsgId);
            reportClientEvent("frame_ack", { ackMs: Date.now() - sentAt });
          }
          setAckedIds((prev) => {
            const next = new Map(prev);
            next.set(control.clientMsgId, control.seq ?? null);
            while (next.size > ACKED_IDS_CAP) {
              const oldest = next.keys().next().value;
              if (oldest === undefined) break;
              next.delete(oldest);
            }
            return next;
          });
        } else if (control.kind === "session_expired") {
          wsDiag("session_expired", { sessionId });
          expiredRef.current = true; // dead session — do not auto-reconnect
          setStatus("expired");
        } else if (control.kind === "session_denied") {
          // Refused identity, not a dead session. Terminal: retrying re-runs
          // the same server-side check and loses the same way.
          wsDiag("session_denied", { sessionId, code: control.code });
          deniedRef.current = true;
          setStatus("denied");
        } else if (control.kind === "unknown_intent") {
          setTimeline((prev) => [
            ...prev,
            {
              kind: "error",
              id: `unknown_intent#${prev.length}`,
              message: tRef.current("chatTop.unknownIntent", { name: control.name }),
              terminal: false,
              localized: true,
            },
          ]);
        }
      },
      onClose: (info) => {
        // A 4403 is the server refusing this IDENTITY. Latch it from the CLOSE
        // CODE and not only from the `session_denied` frame: the frame is sent
        // on the same tick as the close, and a frame lost in that race would
        // otherwise leave a revoked tab indistinguishable from a Cloud Run
        // 1006 — reconnecting forever, since nothing else ever writes a
        // terminal status. (4503, the transient store-unavailable close, is
        // deliberately absent here: it must keep retrying.)
        if (info?.code === WS_CLOSE_DENIED) {
          if (!deniedRef.current) {
            wsDiag("session_denied", { sessionId, code: info.reason, viaClose: true });
          }
          deniedRef.current = true;
        }
        // Preserve the terminal states. The idle-kill sequence writes
        // `idle_killed` during the attach effect's re-run, but the socket's
        // close event (from that same run's cleanup) delivers ASYNCHRONOUSLY
        // — always after the status write. An unconditional "closed" here
        // clobbered it, which made every surface's killed-mode dialog
        // unreachable: the UI kept its live widgets and queued sends into a
        // deliberately-closed socket (the zombie-UI bug).
        setStatus((s) =>
          s === "expired" || s === "idle_killed"
            ? s
            : deniedRef.current
              ? "denied"
              : "closed",
        );
        // Auto-reconnect on a real drop (Cloud Run 1006). Skip when WE closed
        // the socket (unmount / manual reconnect) or the server terminally
        // refused it (session gone / identity denied).
        if (
          deliberateCloseRef.current ||
          expiredRef.current ||
          deniedRef.current
        ) {
          wsDiag("reconnect_skipped", {
            sessionId,
            deliberate: deliberateCloseRef.current,
            expired: expiredRef.current,
            denied: deniedRef.current,
          });
          return;
        }
        const attempt = reconnectAttemptRef.current;
        reconnectAttemptRef.current = attempt + 1;
        const delay = Math.min(500 * 2 ** attempt, 8000); // 0.5s → cap 8s
        wsDiag("reconnect_scheduled", { sessionId, attempt, delayMs: delay });
        if (reconnectTimerRef.current !== null) {
          clearTimeout(reconnectTimerRef.current);
        }
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = null;
          setReconnectKey((k) => k + 1);
        }, delay);
      },
      onProtocolError: (err) =>
        setTimeline((prev) => [
          ...prev,
          {
            kind: "error",
            id: `proto#${prev.length}`,
            message: tRef.current("chatTop.protocolError", { detail: err.message }),
            terminal: false,
            localized: true,
          },
        ]),
    });
    clientRef.current = client;
    return () => {
      if (attachSettleTimerRef.current !== null) {
        clearTimeout(attachSettleTimerRef.current);
        attachSettleTimerRef.current = null;
      }
      attachBurstRef.current = null;
      // Our own teardown (sessionId/reconnectKey change, unmount). Mark it so
      // the resulting onClose doesn't schedule a competing auto-reconnect, and
      // cancel any pending reconnect timer.
      deliberateCloseRef.current = true;
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      client.close();
      clientRef.current = null;
    };
  }, [sessionId, reconnectKey, idleKilled]);

  // Human-idle watchdog. Arms a timer that, on expiry, opens the kill countdown
  // (it does NOT close the socket — the countdown effect below owns the kill).
  // Any user gesture re-arms it. Disabled when there's no session, while the
  // countdown is already showing, or once killed.
  useEffect(() => {
    if (sessionId === null || idleCountdownEndsAt !== null || idleKilled) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onExpire = () => {
      // Never warn mid-turn (a streaming reply would be lost) or during an
      // active autonomous implement run (the server refuses to hard-DELETE a
      // protected run, so the countdown would be a lie). Re-check after a grace.
      if (agentBusyRef.current || implementActiveRef.current) {
        timer = setTimeout(onExpire, IDLE_WARN_MS);
        return;
      }
      setIdleCountdownEndsAt(Date.now() + IDLE_KILL_COUNTDOWN_MS);
    };
    const arm = () => {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(onExpire, IDLE_WARN_MS);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") arm();
    };
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, arm, { passive: true });
    }
    document.addEventListener("visibilitychange", onVisibility);
    arm();
    return () => {
      if (timer !== null) clearTimeout(timer);
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, arm);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [sessionId, idleCountdownEndsAt, idleKilled]);

  // Kill countdown. Active only while `idleCountdownEndsAt` is set. On expiry we
  // hard-DELETE the session (server → cancel CI job → free runner) and latch
  // `idleKilled`. Any human gesture (or the dialog's "I'm still here" →
  // `dismissIdle`) cancels first — the user came back, so we keep the live
  // session exactly as it was, no reconnect.
  useEffect(() => {
    if (idleCountdownEndsAt === null || idleKilled) return;
    const cancel = () => setIdleCountdownEndsAt(null);
    const fire = setTimeout(
      () => {
        if (sessionId !== null) {
          // `?reason=idle` → evict + cancel the pipeline, but do NOT discard.
          // A bare DELETE here stamps `endedAt`, which is never cleared, so
          // going idle used to permanently revoke the session: the next resume
          // booted a sandbox whose every MCP call 401'd and the user watched a
          // spinner forever. Going idle is not a user gesture — reclaim the
          // resources, keep the session resumable. Best-effort: a failed DELETE
          // still leaves the GitLab 60-min job ceiling as the backstop.
          trackIdleDelete(
            sessionId,
            httpDelete(`/sessions/${sessionId}?reason=idle`),
          );
        }
        wsDiag("idle_kill_fired", { sessionId });
        // Outlives the tab, unlike `wsDiag`: an idle kill is the one event that
        // by definition happens with nobody watching.
        reportClientEvent("idle_kill", {
          why: "fired",
          outboxLen: outboxRef.current.length,
        });
        setIdleCountdownEndsAt(null);
        setIdleKilled(true);
      },
      Math.max(0, idleCountdownEndsAt - Date.now()),
    );
    const onVisibility = () => {
      if (document.visibilityState === "visible") cancel();
    };
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, cancel, { passive: true });
    }
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearTimeout(fire);
      for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, cancel);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [idleCountdownEndsAt, idleKilled, sessionId]);

  // Single dispatch seam for every outbound frame: mints the delivery id,
  // enqueues, attempts the write, and applies the capability-dependent release
  // rule (ack-capable server → held until the `ack` control; legacy server →
  // released on an OPEN-handed write, the pre-ack behaviour). Returns the
  // minted id plus whether the write reached an OPEN socket NOW — the boolean
  // the structured senders' retry UIs key on.
  const dispatchFrame = useCallback(
    (
      frame: ClientFrame,
      opts?: { readonly scope?: string },
    ): { clientMsgId: string; delivered: boolean } => {
      const clientMsgId = mintMsgId();
      const stamped = withMsgId(frame, clientMsgId);
      outboxRef.current.push({
        clientMsgId,
        frame: stamped,
        kind: frame.kind,
        enqueuedAt: Date.now(),
        ...(opts?.scope !== undefined ? { scope: opts.scope } : {}),
      });
      const delivered = clientRef.current?.send(stamped) ?? false;
      if (delivered && !acksEnabledRef.current) {
        outboxRef.current = outboxRef.current.filter(
          (e) => e.clientMsgId !== clientMsgId,
        );
      }
      if (!delivered) {
        wsDiag("send_queued", {
          sessionId,
          kind: frame.kind,
          outboxLen: outboxRef.current.length,
        });
      }
      // Report the ATTEMPT to the server. `wsDiag` above only reaches
      // `window`, so without this a frame lost in flight and a frame never
      // sent are indistinguishable in any log that outlives the tab.
      sendAttemptAtRef.current.set(clientMsgId, Date.now());
      reportClientEvent("frame_send", {
        kind: frame.kind,
        delivered,
        outboxLen: outboxRef.current.length,
      });
      return { clientMsgId, delivered };
    },
    [sessionId],
  );

  const send = useCallback(
    (
      text: string,
      opts?: {
        readonly scope?: string;
        readonly flow?: {
          readonly issueIid: number;
          readonly boundary?: boolean;
          /** Display metadata for the discovery list — see `UserMessageFrame`. */
          readonly issueTitle?: string;
        };
        readonly evidenceText?: string;
        readonly newChat?: boolean;
      },
    ): string => {
      if (text.trim() === "") return "";
      // No optimistic append — the server echoes user input back onto the
      // bus as a `user_message` event, which `applyEvent` renders. The
      // round trip is cheap (single WS hop) and the echoed entry survives
      // a reattach (sidebar click, brief disconnect) because it's on
      // `bus.events`; an optimistic local entry would not. Delivery is
      // confirmed by the ingress `ack` (NOT the echo, NOT readyState) —
      // until it arrives the frame sits in the outbox and redelivers on
      // reconnect. `flow` rides the payload so the boundary survives that
      // redelivery (the seed is sent the instant the socket opens on boot —
      // the common case, not an edge).
      return dispatchFrame(
        {
          kind: "user_message",
          payload: {
            text,
            ...(opts?.flow ? { flow: opts.flow } : {}),
            // `!== undefined`, NOT truthiness: `""` is the refine seed's real
            // answer ("nothing in this turn is quotable") and dropping it would
            // silently hand the whole machine-composed seed back to the gate as
            // user evidence — the exact laundering this field exists to close.
            ...(opts?.evidenceText !== undefined
              ? { evidenceText: opts.evidenceText }
              : {}),
            ...(opts?.newChat ? { newChat: true } : {}),
          },
        },
        opts,
      ).clientMsgId;
    },
    [dispatchFrame],
  );

  const sendSystem = useCallback(
    (text: string) => {
      if (text.trim() === "") return;
      // Mode-toggle slashes intentionally do not echo into the timeline —
      // the UI surfaces them via the segmented control instead. They produce
      // no bus echo, which is exactly why the explicit ingress ack (rather
      // than echo-watching) had to be the delivery signal: before it, a flip
      // written into a dead-but-OPEN socket was silently lost.
      dispatchFrame({ kind: "user_message", payload: { text } });
    },
    [dispatchFrame],
  );

  // Accept an all-green card. Sends the structured frame; the server applies it
  // deterministically and replies with `readiness_apply_result`. Returns
  // whether the frame reached an OPEN socket so the caller can show a retry if
  // the socket was mid-flap. Additionally outboxed (ack-released): the server
  // apply is idempotent on a consumed token, so an ack-less redelivery on the
  // next attach is a safe no-op.
  const sendReadinessAccept = useCallback(
    (issueIid: number, acceptToken: string): boolean =>
      dispatchFrame({
        kind: "readiness_accept",
        payload: { issueIid, acceptToken },
      }).delivered,
    [dispatchFrame],
  );

  // Edit a field on the ready preview. Widget fields resolve server-side in one
  // round-trip; free-text fields fold in via a directed agent re-propose. The
  // server acks both variants, so an ack-less redelivery on the next attach is
  // a safe no-op (widget: re-greens the same value; free-text: re-records the
  // same pending edit).
  const sendReadinessFieldEdit = useCallback(
    (issueIid: number, field: string, text: string): boolean =>
      dispatchFrame({
        kind: "readiness_field_edit",
        payload: { issueIid, field, text },
      }).delivered,
    [dispatchFrame],
  );

  // Withdraw a pending free-text edit ("Keep the previous value"). Same delivery
  // semantics; the server drops the pending entry and re-emits the card.
  const sendReadinessFieldEditCancel = useCallback(
    (issueIid: number, field: string): boolean =>
      dispatchFrame({
        kind: "readiness_field_edit",
        payload: { issueIid, field, cancel: true },
      }).delivered,
    [dispatchFrame],
  );

  // Skip an optional field. Idempotent server-side (the field is added to a set),
  // so an ack-less redelivery on the next attach is a safe no-op.
  const sendReadinessFieldDecline = useCallback(
    (issueIid: number, field: string): boolean =>
      dispatchFrame({
        kind: "readiness_field_decline",
        payload: { issueIid, field },
      }).delivered,
    [dispatchFrame],
  );

  // Answer typed pre-card asks. Returns the minted clientMsgId (see the
  // interface doc — the seq-keyed flush suppression keys on it, so returning
  // `.delivered` here would silently disable it). Outboxed + ack-released like
  // every frame; the payload's `issueIid` keeps a redelivery bound to its flow.
  const sendQuestionAnswer = useCallback(
    (
      issueIid: number,
      text: string,
      answers: ReadonlyArray<{
        readonly askId: string;
        readonly field: string | null;
        readonly text: string;
        readonly kind?: "question" | "concern";
      }>,
      declinedAskIds?: ReadonlyArray<string>,
      extra?: {
        readonly cardAnswers?: ReadonlyArray<{
          readonly field: string;
          readonly text: string;
        }>;
        readonly freeText?: string;
        readonly answeredCount?: number;
        readonly scope?: string;
      },
    ): string =>
      dispatchFrame(
        {
          kind: "question_answer",
          payload: {
            issueIid,
            text,
            answers,
            ...(declinedAskIds !== undefined && declinedAskIds.length > 0
              ? { declinedAskIds }
              : {}),
            ...(extra?.cardAnswers !== undefined && extra.cardAnswers.length > 0
              ? { cardAnswers: extra.cardAnswers }
              : {}),
            ...(extra?.freeText !== undefined && extra.freeText.length > 0
              ? { freeText: extra.freeText }
              : {}),
            ...(extra?.answeredCount !== undefined
              ? { answeredCount: extra.answeredCount }
              : {}),
          },
        },
        // Scoped like the plain `send` path: an answer whose surface has moved
        // on must be dropped from the outbox, not redelivered into another
        // issue's flow where it kills a live turn and grounds nothing.
        extra?.scope !== undefined ? { scope: extra.scope } : undefined,
      ).clientMsgId,
    [dispatchFrame],
  );

  // Request the end-of-refine implementation simulation. Returns whether the
  // frame reached an OPEN socket so the surface can fall through to Accept if
  // not. Outboxed like every frame; the server's ingress dedupe absorbs a
  // redelivery that races the retry UI.
  const sendReadinessPlanRequest = useCallback(
    (issueIid: number, correction?: string): boolean =>
      dispatchFrame({
        kind: "readiness_plan_request",
        payload: {
          issueIid,
          ...(correction !== undefined ? { correction } : {}),
        },
      }).delivered,
    [dispatchFrame],
  );

  // Confirm the business summary. Idempotent server-side — and only since the
  // confirm stopped rewriting `implementationPlan.updatedAtMs`: that stamp IS
  // the revision this frame binds to, so writing it on confirm meant the second
  // delivery of the same decision named a revision the store no longer held and
  // took the refusal branch. An ack-less redelivery on the next attach is now
  // the no-op this comment always claimed. A stale revision is still refused
  // rather than applied, which is why the caller passes the one it rendered.
  const sendReadinessPlanConfirm = useCallback(
    (issueIid: number, planRevision: number): boolean =>
      dispatchFrame({
        kind: "readiness_plan_confirm",
        payload: { issueIid, planRevision },
      }).delivered,
    [dispatchFrame],
  );

  // Raise the spend ceiling in place. Clears the local `budgetExhausted` latch
  // optimistically so the flow returns to whatever screen it was on — the
  // server re-arms its own latch, so a genuine second exhaustion still lands.
  const sendBudgetExtend = useCallback(
    (addUsd: number): boolean => {
      const { delivered } = dispatchFrame({
        kind: "budget_extend",
        payload: { addUsd },
      });
      if (delivered) {
        // `budgetExhausted` is DERIVED from the timeline, so it cannot be
        // "unset" — dismiss the specific entry instead. The id is bus-seq
        // derived, so it survives the reconnect timeline wipe + replay, and a
        // genuine SECOND exhaustion arrives as a NEW id and shows again.
        const latest = [...timeline]
          .reverse()
          .find((e) => e.kind === "budget_exhausted");
        if (latest) setExtendedBudgetEntryId(latest.id);
      }
      return delivered;
    },
    [dispatchFrame, timeline],
  );

  // Request the epic/sub-issue breakdown proposal. Same delivery semantics as
  // the plan request (outboxed; ingress-deduped on redelivery).
  const sendReadinessBreakdownRequest = useCallback(
    (issueIid: number, force?: boolean): boolean =>
      dispatchFrame({
        kind: "readiness_breakdown_request",
        payload: {
          issueIid,
          ...(force === true ? { force: true } : {}),
        },
      }).delivered,
    [dispatchFrame],
  );

  // Figma chip actions. Outboxed like every other structured frame: a decline
  // lost to a dead-but-OPEN socket redelivers on the next attach, where the
  // ingress dedupe drops the duplicate — which matters more here than
  // elsewhere, because a decline that never lands is a prompt that comes back
  // forever.
  const sendFigmaConnectDeclined = useCallback(
    (linkId: string, scope: "link" | "session"): boolean =>
      dispatchFrame({
        kind: "figma_connect_declined",
        payload: { linkId, scope },
      }).delivered,
    [dispatchFrame],
  );

  const sendFigmaAllowFile = useCallback(
    (linkId: string): boolean =>
      dispatchFrame({
        kind: "figma_allow_file",
        payload: { linkId },
      }).delivered,
    [dispatchFrame],
  );

  // "Is this the screen you meant?" — yes or no. Carries NO text, by design:
  // see `FigmaPreviewVerdictFrame`. Outboxed like every other card action, so a
  // verdict lost to a dead socket redelivers instead of leaving the ask pinned.
  const sendFigmaPreviewVerdict = useCallback(
    (previewId: string, askId: string, verdict: "right" | "wrong"): boolean =>
      dispatchFrame({
        kind: "figma_preview_verdict",
        payload: { previewId, askId, verdict },
      }).delivered,
    [dispatchFrame],
  );

  // Stable per MAP identity, which is what makes the answer usable as a React
  // DEP. `useDesignScene` lists its `persisted` scene in the effect's deps; the
  // fold returns the SAME map (and so the same scene object) when nothing
  // changed, so a re-render cannot make a settled widget re-decide which lane
  // it is on.
  const figmaSceneFor = useCallback(
    (handle: string): DesignScene | null =>
      figmaScenes.get(handle)?.scene ?? null,
    [figmaScenes],
  );

  // Confirm the breakdown. The server sequences parent apply → children
  // creation; both halves are idempotent (consumed token / per-child
  // receipts), so redelivery and user retries are safe.
  const sendBreakdownAccept = useCallback(
    (issueIid: number, acceptToken: string, breakdownToken: string): boolean =>
      dispatchFrame({
        kind: "readiness_breakdown_accept",
        payload: { issueIid, acceptToken, breakdownToken },
      }).delivered,
    [dispatchFrame],
  );

  // Typed split adjustment. Outboxed like every other card action, so an op
  // lost to a dead socket redelivers rather than silently vanishing — the CAS
  // on `baseChildrenHash` is what makes that redelivery safe.
  const sendBreakdownOp = useCallback(
    (
      issueIid: number,
      baseChildrenHash: string,
      op: BreakdownOpPayload,
      groundingText?: string,
    ): boolean => {
      // A new attempt supersedes the last refusal — this is the one moment the
      // banner is genuinely stale. Clearing it on the next `breakdown_ready`
      // instead would erase it immediately, because a refusal and the re-emit
      // of the current card ride together.
      setBreakdownOpResult(null);
      return dispatchFrame({
        kind: "readiness_breakdown_op",
        payload: {
          issueIid,
          baseChildrenHash,
          op,
          ...(groundingText !== undefined ? { groundingText } : {}),
        },
      }).delivered;
    },
    [dispatchFrame],
  );

  // Resolve a DoR-halted implement run by proceeding anyway. Returns whether
  // the frame reached an OPEN socket. Outboxed: a "proceed" lost to a dead
  // socket previously stranded the halted run with no retry surface at all;
  // now it redelivers on reconnect (`run.dorCleared` gates duplicates).
  const sendImplementProceed = useCallback(
    (): boolean =>
      dispatchFrame({
        kind: "implement_decision",
        payload: { decision: "proceed" },
      }).delivered,
    [dispatchFrame],
  );

  // Bind/release the active send-scope (see OutboxEntry.scope). The owning
  // surface (RefineFlow) sets it on mount and clears it on unmount.
  const setSendScope = useCallback((scope: string | null) => {
    activeSendScopeRef.current = scope;
  }, []);

  const reconnect = useCallback(() => {
    setReconnectKey((k) => k + 1);
  }, []);

  // Reset is the ONE flow that legitimately restarts the server bus seq under
  // the SAME sessionId (`POST /sessions/:id/reset` wipes the event log; the
  // rebuilt bus counts from 0). Everything client-side that assumes per-session
  // seq monotonicity or id stability — the readiness-card latches, every
  // uiState key carrying an entry id — must be invalidated with it, or the
  // latch wedges on the pre-reset high-water seq and reborn entry ids collide
  // with persisted one-shot latches ("Sent" buttons born consumed). A plain
  // reconnect() must NOT do any of this: surviving the reconnect wipe is the
  // store's whole purpose.
  const resetSession = useCallback(() => {
    uiState.clear();
    // The outbox and ack map are seq/context-bound to the pre-reset bus: a
    // queued pre-reset answer redelivered into the post-reset session would
    // ground against a wiped event log (or worse, a different draft), and
    // pre-reset echo seqs are meaningless against the rebuilt seq-0 bus.
    if (outboxRef.current.length > 0) {
      wsDiag("outbox_dropped_reset", { count: outboxRef.current.length });
    }
    outboxRef.current = [];
    setAckedIds(new Map());
    // Sizing/breakdown side-channel states are pre-reset context — a stale
    // terminal result or degrade must not leak across the rebuilt seq-0 bus.
    setSizingResult(null);
    setBreakdownUnavailable(null);
    setBreakdownProgress(null);
    setBreakdownResult(null);
    setBreakdownOpResult(null);
    setResetEpoch((e) => e + 1);
    setReconnectKey((k) => k + 1);
  }, [uiState]);

  // Cancel a pending human-idle kill (the user is back). The socket stayed open
  // through the countdown, so there's nothing to reopen — just drop the deadline
  // and let the watchdog re-arm.
  const dismissIdle = useCallback(() => {
    reportClientEvent("idle_kill", { why: "dismissed" });
    setIdleCountdownEndsAt(null);
  }, []);

  // Resume after the idle HARD-kill (post-DELETE, `idle_killed` latched). Unlike
  // `dismissIdle`, the socket here is deliberately shut, so we must reopen it:
  // clearing `idleKilled` re-runs the WS-attach effect (idleKilled is in its dep
  // array), which takes the normal attach path and opens a fresh socket onto the
  // same session. The countdown is already null by this point, but clear it
  // defensively. `status` is set to "connecting" so the terminal "idle_killed"
  // screen doesn't flash between this state write and the effect re-run (which
  // sets the same status itself). The caller drives `POST .../reconnect` FIRST
  // and only calls this once the server has re-hydrated the session.
  const resumeFromIdleKill = useCallback(() => {
    if (sessionId !== null) clearIdleDelete(sessionId);
    setIdleKilled(false);
    setIdleCountdownEndsAt(null);
    setStatus("connecting");
  }, [sessionId]);

  const clearReauth = useCallback(() => {
    setReauth({ required: false, gitlabHost: "" });
    setReconnectKey((k) => k + 1);
  }, []);

  // F5 — the terminal budget state, derived from the durable timeline entry so
  // it survives a reconnect replay (single source, like applyResult/planResult).
  const budgetExhausted = useMemo<UseWsSession["budgetExhausted"]>(() => {
    for (let i = timeline.length - 1; i >= 0; i -= 1) {
      const e = timeline[i];
      if (e && e.kind === "budget_exhausted") {
        // The user chose "keep going" on THIS wall — the ceiling was raised on
        // the live bridge, so the entry is history, not the current state.
        if (e.id === extendedBudgetEntryId) return null;
        return { budget: e.budget, limit: e.limit, used: e.used };
      }
    }
    return null;
  }, [timeline, extendedBudgetEntryId]);

  // The sandbox could not reach Loom's tools, so no readiness card can ever
  // arrive. Terminal for this session — derived from the durable timeline entry
  // so it survives a reconnect replay, exactly like `budgetExhausted`.
  const toolsUnavailable = useMemo<UseWsSession["toolsUnavailable"]>(() => {
    for (let i = timeline.length - 1; i >= 0; i -= 1) {
      const e = timeline[i];
      if (e && e.kind === "tools_unavailable") {
        return { reason: e.reason, detail: e.detail };
      }
    }
    return null;
  }, [timeline]);

  return {
    timeline,
    status,
    reauth,
    bridge,
    sandbox,
    mode,
    branch,
    agentBusy,
    busyKnown,
    agentWork,
    applyResult,
    budgetExhausted,
    toolsUnavailable,
    planResult,
    sizingResult,
    breakdownUnavailable,
    breakdownProgress,
    breakdownResult,
    send,
    sendSystem,
    sendReadinessAccept,
    sendReadinessFieldEdit,
    sendReadinessFieldEditCancel,
    sendReadinessFieldDecline,
    sendQuestionAnswer,
    sendReadinessPlanRequest,
    sendReadinessPlanConfirm,
    sendBudgetExtend,
    sendReadinessBreakdownRequest,
    sendBreakdownAccept,
    sendBreakdownOp,
    breakdownOpResult,
    figmaLinks,
    sendFigmaConnectDeclined,
    sendFigmaAllowFile,
    sendFigmaPreviewVerdict,
    /** This deployment serves the design sketch (`session_attached`'s
     *  `figmaPreview`). Gates every "Show the sketch" affordance — without it a
     *  chip offers a drawing the server has no route for. */
    figmaPreviewEnabled,
    /** The picture this chat had for a handle, or null — see the interface. */
    figmaSceneFor,
    implement,
    sendImplementProceed,
    ackedIds,
    setSendScope,
    uiState,
    reconnect,
    resetSession,
    resetEpoch,
    idleCountdownEndsAt,
    dismissIdle,
    resumeFromIdleKill,
    clearReauth,
  };
};
